import React, { useState, useEffect, useMemo, useContext, useImperativeHandle, forwardRef, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormLabel,
  Chip
} from '@mui/material';
import {
  Save as SaveIcon,
  Calculate as CalculateIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ProductFormData } from '../types';
import { productsApi, measurementsApi, templatesApi, formSettingsApi } from '../services/api';
import { UserRoleContext } from './Layout';
import { useEditSession } from '../hooks/useEditSession';
import EditSessionWarning from './EditSessionWarning';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { Checkbox, FormControlLabel } from '@mui/material';
import Snackbar from '@mui/material/Snackbar';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Autocomplete from '@mui/material/Autocomplete';
import { createFilterOptions } from '@mui/material/Autocomplete';

const formSchema = z.object({
  productType: z.string().min(1, 'Tip proizvoda je obavezan'),
  datumProizvodnje: z.string().min(1, 'Datum proizvodnje je obavezan'),
  vremeProizvodnje: z.string().min(1, 'Vreme proizvodnje je obavezno'),
  kontrolisao: z.string().min(1, 'Kontrolisao je obavezan'),
  
  // Gustina sekcija
  masaRolne: z.number().min(0.001, 'Masa rolne mora biti veća od 0'),
  zapremina: z.number().min(0.001, 'Zapremina mora biti veća od 0'),
  gustina: z.number().optional(),
  
  // Suva materija
  m1: z.number().min(0, 'm1 mora biti veće ili jednako 0'),
  m2: z.number().min(0, 'm2 mora biti veće ili jednako 0'),
  m3: z.number().min(0, 'm3 mora biti veće ili jednako 0'),
  
  // Izmerene vrednosti dužine
  duzina200: z.number().min(0, 'Dužina 200mm mora biti veća ili jednaka 0'),
  duzina600: z.number().min(0, 'Dužina 600mm mora biti veća ili jednaka 0'),
  duzina1000: z.number().min(0, 'Dužina 1000mm mora biti veća ili jednaka 0'),
  
  // Izmerene vrednosti širine
  sirina200: z.number().min(0, 'Širina 200mm mora biti veća ili jednaka 0'),
  sirina1200: z.number().min(0, 'Širina 1200mm mora biti veća ili jednaka 0'),
  sirina2200: z.number().min(0, 'Širina 2200mm mora biti veća ili jednaka 0'),
  sirina3200: z.number().min(0, 'Širina 3200mm mora biti veća ili jednaka 0'),
  sirina4200: z.number().min(0, 'Širina 4200mm mora biti veća ili jednaka 0'),
  sirina5200: z.number().min(0, 'Širina 5200mm mora biti veća ili jednaka 0'),
  
  // Dodatne dužine za širinu
  duzinaSirina1: z.number().min(0, 'Dužina 1 mora biti veća ili jednaka 0').optional(),
  duzinaSirina2: z.number().min(0, 'Dužina 2 mora biti veća ili jednaka 0').optional(),
  duzinaSirina3: z.number().min(0, 'Dužina 3 mora biti veća ili jednaka 0').optional(),
  
  // Distribucija vlakana
  distribucija1: z.number().min(0, 'Distribucija 1 mora biti veća ili jednaka 0'),
  distribucija2: z.number().min(0, 'Distribucija 2 mora biti veća ili jednaka 0'),
  distribucija3: z.number().min(0, 'Distribucija 3 mora biti veća ili jednaka 0'),
  distribucija4: z.number().min(0, 'Distribucija 4 mora biti veća ili jednaka 0'),
  distribucija5: z.number().min(0, 'Distribucija 5 mora biti veća ili jednaka 0'),
  distribucija6: z.number().min(0, 'Distribucija 6 mora biti veća ili jednaka 0'),
  
  // Referentna vrednost
  debljina1: z.number().min(0, 'Debljina 1 mora biti veća ili jednaka 0'),
  debljinaUzorak1: z.number().min(0, 'Debljina uzorak 1 mora biti veća ili jednaka 0'),
  debljinaUzorak2: z.number().min(0, 'Debljina uzorak 2 mora biti veća ili jednaka 0'),
  debljinaUzorak3: z.number().min(0, 'Debljina uzorak 3 mora biti veća ili jednaka 0'),
  pretvaranje: z.number().min(0, 'Pretvaranje mora biti veće ili jednako 0'),
  
  // AFr sekcija - Uzorak 1
  deltaP1: z.number().min(0, 'ΔP (Pa) uzorak 1 mora biti veće ili jednako 0'),
  
  // AFr sekcija - Uzorak 2
  deltaP2: z.number().min(0, 'ΔP (Pa) uzorak 2 mora biti veće ili jednako 0'),
  
  // AFr sekcija - Uzorak 3
  deltaP3: z.number().min(0, 'ΔP (Pa) uzorak 3 mora biti veće ili jednako 0'),
  duzineRows: z.array(z.object({ duzina: z.number(), izmerena: z.number() })).optional(),
  sirineRows: z.array(z.object({ sirina: z.number(), izmerena: z.number() })).optional(),
  napomena: z.string().optional(),
  smena: z.string().min(1, 'Smena je obavezna'),
  datumTestiranja: z.string().optional(),
  vremeTestiranja: z.string().optional(),
  vremePakovanja: z.string().optional(),
  secena: z.string().min(1, 'Obavezno polje'),
  pozicijaSecenja: z.string().optional(),
  lambdaVrednost: z.number().optional(),
  lambdaGustinga: z.number().optional(),
  cs10: z.number().optional(),
  verified: z.boolean().optional(),
  verifiedBy: z.string().optional(),
  notVerified: z.boolean().optional(),
  notVerifiedBy: z.string().optional(),
  rows: z.array(z.any()).optional(),
  lambda9090: z.number().optional(),
});

// type FormData = z.infer<typeof formSchema>;
type FormData = z.infer<typeof formSchema>;

const POINTS_PRESET: Array<{ type: 'D' | 'L'; label: string; length: number; width: number }> = [
  { type: 'D', label: 'D1', length: 200, width: 200 },
  { type: 'D', label: 'D2', length: 600, width: 310 },
  { type: 'D', label: 'D3', length: 1000, width: 420 },
  { type: 'D', label: 'D4', length: 1400, width: 530 },
  { type: 'L', label: 'L1', length: 1750, width: 640 },
  { type: 'L', label: 'L2', length: 2250, width: 750 },
  { type: 'L', label: 'L3', length: 2750, width: 860 },
  { type: 'L', label: 'L4', length: 3250, width: 1000 },
  { type: 'L', label: 'L5', length: 3750, width: 930 },
  { type: 'L', label: 'L6', length: 4250, width: 860 },
  { type: 'L', label: 'L7', length: 4750, width: 750 },
  { type: 'L', label: 'L8', length: 5250, width: 640 },
  { type: 'D', label: 'D5', length: 5750, width: 530 },
  { type: 'D', label: 'D6', length: 6250, width: 420 },
  { type: 'D', label: 'D7', length: 6750, width: 310 },
  { type: 'D', label: 'D8', length: 7250, width: 200 },
];

interface PointRow {
  type: 'D' | 'L';
  label: string;
  length: number;
  width: number;
  measured: number | '';
  caliper: number | '';
}

const getDefaultRows = (): PointRow[] =>
  POINTS_PRESET.map(p => ({ ...p, measured: '', caliper: '' }));

const getPresetForType = (type: 'D' | 'L', index: number) => {
  // Find the nth D or L in preset
  let count = 0;
  for (let i = 0; i < POINTS_PRESET.length; i++) {
    if (POINTS_PRESET[i].type === type) {
      if (count === index) return POINTS_PRESET[i];
      count++;
    }
  }
  // fallback
  return { type, label: type + (index + 1), length: 0, width: 0 };
};

// Prilagođeni tip za red dužine
interface DuzinaRow {
  duzina: number;
  izmerena: number;
}

// Prilagođeni tip za red širine
interface SirinaRow {
  sirina: number;
  izmerena: number;
}

interface Product {
  _id: string;
  name: string;
  width: number;
  thickness: number;
  length: number;
  lambdaGroup?: string;
}

// Dodaj prop za template režim
interface ProductFormProps {
  templateMode?: boolean;
  templateProductId?: string;
  onTemplateSave?: () => void;
}

// Izmeni deklaraciju komponente na forwardRef
const ProductForm = forwardRef<{ handleSaveTemplate: () => void }, ProductFormProps>(
  ({ templateMode = false, templateProductId, onTemplateSave }, ref) => {
    // Helper function to disable scroll and arrow increment/decrement on number inputs
    const getNumberInputProps = () => ({
      onWheel: (e: any) => (e.target as HTMLInputElement).blur(),
      onKeyDown: (e: any) => {
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
          e.preventDefault();
        }
      }
    });
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Inicijalno prazni redovi dok nije izabran proizvod
    const [rows, setRows] = useState<PointRow[]>([]);
    const { role } = useContext(UserRoleContext);

    const [productOptions, setProductOptions] = useState<Product[]>([]);
    const [loadingProducts, setLoadingProducts] = useState(false);

    const userFromStorage = JSON.parse(localStorage.getItem('user') || '{}');
    
    // State za edit mode indikator
    const [showEditIndicator, setShowEditIndicator] = useState(false);

    // Edit session management
    const editSession = useEditSession({
      resourceId: id || 'new',
      resourceType: 'measurement',
      resourceName: `Merenje ${id || 'novo'}`
    });

    // Debug log za sessionTakenOver stanje
    // console.log('🔍 ProductForm render - sessionTakenOver:', editSession.sessionTakenOver, 'isEditing:', editSession.isEditing, 'sessionId:', editSession.sessionId);

    const isYellowField = (fieldName: string) => {
      const yellowFields = [
        // Osnovni podaci
        'datumProizvodnje',
        'vremeProizvodnje',
        
        // Gustina sekcija
        'masaRolne',
        'm1', 'm2', 'm3',
        
        // Izmerene vrednosti dužine
        'duzina200', 'duzina600', 'duzina1000',
        
        // Izmerene vrednosti širine
        'sirina200', 'sirina1200', 'sirina2200', 'sirina3200', 
        'sirina4200', 'sirina5200',
        
        // Dodatne dužine za širinu
        'duzinaSirina1', 'duzinaSirina2', 'duzinaSirina3',
        
        // Distribucija vlakana
        'distribucija1', 'distribucija2', 'distribucija3', 
        'distribucija4', 'distribucija5', 'distribucija6',
        
        // Referentna vrednost
        'debljina1', 'debljinaUzorak1', 'debljinaUzorak2', 
        'debljinaUzorak3', 'pretvaranje',
        
        // AFr sekcija
        'deltaP1', 'deltaP2', 'deltaP3',
        
        // Tabela debljine
        'measured', // Izmerena vrednost u tabeli
        'caliper'   // Šubler u tabeli (za admin)
      ];
      return yellowFields.includes(fieldName);
    };

    const getSmena = () => {
      const now = new Date();
      const hours = now.getHours();
      if (hours >= 6 && hours < 14) return 'I';
      if (hours >= 14 && hours < 22) return 'II';
      return 'III';
    };

    const {
      control,
      handleSubmit,
      watch,
      setValue,
      formState,
      ...rest
    } = useForm<FormData>({
      resolver: zodResolver(formSchema),
      mode: 'onChange',
      defaultValues: {
        productType: '',
        datumProizvodnje: '',
        vremeProizvodnje: '',
        kontrolisao: userFromStorage.username || '',
        masaRolne: 0.001,
        zapremina: 0.9,
        gustina: 0,
        m1: 0, m2: 0, m3: 0,
        duzina200: 0, duzina600: 0, duzina1000: 0,
        sirina200: 0, sirina1200: 0, sirina2200: 0, sirina3200: 0, sirina4200: 0, sirina5200: 0,
        duzinaSirina1: 0, duzinaSirina2: 0, duzinaSirina3: 0,
        distribucija1: 0, distribucija2: 0, distribucija3: 0, distribucija4: 0, distribucija5: 0, distribucija6: 0,
        debljina1: 37,
        debljinaUzorak1: 0, debljinaUzorak2: 0, debljinaUzorak3: 0, pretvaranje: 100,
        deltaP1: 0, deltaP2: 0, deltaP3: 0,
        duzineRows: [],
        sirineRows: [],
        napomena: '',
        smena: '',
        datumTestiranja: '',
        vremeTestiranja: '',
        vremePakovanja: '',
        secena: '',
        pozicijaSecenja: '',
        lambdaVrednost: 0,
        lambdaGustinga: 0,
        cs10: 0,
        verified: false,
        verifiedBy: '',
        notVerified: false,
        notVerifiedBy: '',
        rows: [],
        lambda9090: undefined,
      }
    });

    const watchedValues = watch();

    useEffect(() => {
      setLoadingProducts(true);
      productsApi.getAll()
        .then(setProductOptions)
        .catch(() => setProductOptions([]))
        .finally(() => setLoadingProducts(false));
    }, []);

    useEffect(() => {
      if (rows.length > 0 && rows[0].caliper !== '') {
        setRows(prev => prev.map((row, idx) => idx === 0 ? row : { ...row, caliper: prev[0].caliper }));
      }
      // eslint-disable-next-line
    }, [rows[0]?.caliper]);

    // Proveri edit sesiju kada se komponenta učita
    useEffect(() => {
      if (id && !templateMode) {
        editSession.checkSession();
      }
    }, [id, templateMode, editSession.checkSession]);

    // Automatski započni sesiju kada se forma učita za postojeće merenje
    useEffect(() => {
      // console.log('🔍 ProductForm useEffect startSession check:', {
      //   id,
      //   templateMode,
      //   isEditing: editSession.isEditing,
      //   warning: editSession.warning,
      //   shouldStart: id && !templateMode && !editSession.isEditing && !editSession.warning
      // });
      
      if (id && !templateMode && !editSession.isEditing && !editSession.warning) {
        // console.log('🔍 ProductForm calling startSession...');
        editSession.startSession();
      }
    }, [id, templateMode, editSession.isEditing, editSession.warning, editSession.startSession]);

    const loadExistingData = async () => {
      try {
        setLoading(true);
        const data = await measurementsApi.getById(id!);
        // Pronađi proizvod na osnovu productType
        const selected = productOptions.find(p => p.name === data.productType);
        let template: any = null;
        if (selected) {
          try {
            template = await templatesApi.getByProductId(selected._id);
          } catch (err) {
            template = null;
          }
        }
        // ... postojeći kod za setValue ...
        setValue('productType', data.productType);
        setValue('datumProizvodnje', data.datumProizvodnje);
        setValue('vremeProizvodnje', data.vremeProizvodnje);
        setValue('kontrolisao', data.kontrolisao);
        setValue('napomena', data.napomena);
        setValue('smena', data.smena);
        setValue('secena', (data as any).secena);
        setValue('pozicijaSecenja', (data as any).pozicijaSecenja);
        setValue('lambdaVrednost', (data as any).lambdaVrednost);
        setValue('lambdaGustinga', (data as any).lambdaGustinga);
        setValue('cs10', (data as any).cs10);
        setValue('lambda9090', (data as any).lambda9090);
        setValue('datumTestiranja', data.datumTestiranja);
        setValue('masaRolne', data.masaRolne);
        setValue('zapremina', data.zapremina);
        setValue('gustina', data.gustina);
        setValue('m1', data.m1);
        setValue('m2', data.m2);
        setValue('m3', data.m3);
        setValue('distribucija1', data.distribucija1);
        setValue('distribucija2', data.distribucija2);
        setValue('distribucija3', data.distribucija3);
        setValue('distribucija4', data.distribucija4);
        setValue('distribucija5', data.distribucija5);
        setValue('distribucija6', data.distribucija6);
        setValue('debljina1', data.debljina1);
        setValue('debljinaUzorak1', data.debljinaUzorak1);
        setValue('debljinaUzorak2', data.debljinaUzorak2);
        setValue('debljinaUzorak3', data.debljinaUzorak3);
        setValue('pretvaranje', data.pretvaranje);
        setValue('deltaP1', data.deltaP1);
        setValue('deltaP2', data.deltaP2);
        setValue('deltaP3', data.deltaP3);

        // --- SINHRONIZACIJA DUZINE ---
        let normalizedDuzineRows = Array.isArray(data.duzineRows)
          ? data.duzineRows.map(row => ({
            duzina: typeof row.duzina === 'number' ? row.duzina : Number(row.duzina) || 0,
            izmerena: typeof row.izmerena === 'number' ? row.izmerena : Number(row.izmerena) || 0
            }))
          : [{ duzina: 0, izmerena: 0 }];
        if (template && Array.isArray(template.duzine)) {
          const duzineSet = new Set(normalizedDuzineRows.map(r => r.duzina));
          template.duzine.forEach((t: any) => {
            if (!duzineSet.has(t.duzina)) {
              normalizedDuzineRows.push({ duzina: t.duzina, izmerena: 0 });
            }
          });
        }
          setDuzineRows(normalizedDuzineRows);
          setValue('duzineRows', normalizedDuzineRows);

        // --- SINHRONIZACIJA SIRINE ---
        let normalizedSirineRows = Array.isArray(data.sirineRows)
          ? data.sirineRows.map(row => ({
            sirina: typeof row.sirina === 'number' ? row.sirina : Number(row.sirina) || 0,
            izmerena: typeof row.izmerena === 'number' ? row.izmerena : Number(row.izmerena) || 0
            }))
          : [{ sirina: 0, izmerena: 0 }];
        if (template && Array.isArray(template.sirine)) {
          const sirineSet = new Set(normalizedSirineRows.map(r => r.sirina));
          template.sirine.forEach((t: any) => {
            if (!sirineSet.has(t.sirina)) {
              normalizedSirineRows.push({ sirina: t.sirina, izmerena: 0 });
            }
          });
        }
          setSirineRows(normalizedSirineRows);
          setValue('sirineRows', normalizedSirineRows);

        // --- SINHRONIZACIJA DEBLJINE ---
        let normRows = Array.isArray(data.rows)
          ? data.rows.map(row => ({
            ...row,
              type: row.type === 'D' ? 'D' : 'L',
              measured: row.measured !== undefined && row.measured !== null ? String(row.measured) : '',
              caliper: row.caliper !== undefined && row.caliper !== null ? String(row.caliper) : ''
            }))
          : [{ type: 'D', label: 'D1', length: 0, width: 0, measured: '', caliper: '' }];
        if (template && Array.isArray(template.debljine)) {
          const labelSet = new Set(normRows.map(r => r.label));
          template.debljine.forEach((t: any, i: number) => {
            if (!labelSet.has(t.label)) {
              normRows.push({
                type: t.type === 'D' ? 'D' : 'L',
                label: t.label || `D${i + 1}`,
                length: typeof t.length === 'number' ? t.length : 0,
                width: typeof t.width === 'number' ? t.width : 0,
                measured: '',
                caliper: ''
              });
            }
          });
        }
        setRows([...normRows] as PointRow[]);

      } catch (err) {
        setError('Greška pri učitavanju podataka');
      } finally {
        setLoading(false);
      }
    };

    // Kada se otvara postojeće merenje (id postoji), učitaj podatke u formu
    useEffect(() => {
      if (id && !templateMode) {
        loadExistingData();
      }
    }, [id, templateMode]);

    const onSubmit = async (data: FormData) => {
      // console.log('Form data:', data);
      
      // Proveri da li je potrebno započeti sesiju
      if (id && !templateMode && !editSession.isEditing) {
        const sessionStarted = await editSession.startSession();
        if (!sessionStarted) {
          setError('Ne možete editujati ovaj proizvod jer je već u upotrebi');
          return;
        }
      }
      
      try {
        setLoading(true);
        setError(null);
        const selectedProduct = productOptions.find(p => p.name === data.productType);
        const productLabel = selectedProduct ? `${selectedProduct.name} ${selectedProduct.width}` : data.productType;
        let template = null;
        if (selectedProduct) {
          template = await templatesApi.getByProductId(selectedProduct._id);
        }
        // Pribavi izračunate vrednosti pre slanja
        const gustina = gustinaProizvoda !== '' ? Number(gustinaProizvoda) : null;
        const zapremina = zapreminaProizvoda !== '' ? Number(zapreminaProizvoda) : null;
        const avgAFr = typeof afrCalculations.avgAFr === 'number' ? afrCalculations.avgAFr : null;
        const m4 = typeof suvaMaterijaCalculations.m4 === 'number' ? suvaMaterijaCalculations.m4 : null;
        const procenatSM = typeof suvaMaterijaCalculations.procenatSM === 'number' ? suvaMaterijaCalculations.procenatSM : null;
        // KORISTI DINAMIČKE PROSEKE AKO POSTOJE
        const avgDuzinaVal = avgDuzinaDynamic !== '' ? Number(avgDuzinaDynamic) : null;
        const avgSirinaVal = avgSirinaDynamic !== '' ? Number(avgSirinaDynamic) : null;
        const avgDistribucijaVal = avgDistribucija !== '' ? Number(avgDistribucija) : null;
        const avgThicknessVal = avgThickness !== '' ? Number(avgThickness) : null;
        const actualThicknessVal = Array.isArray(actualThickness) ? actualThickness : [];

        // Logika za status verifikacije
        let verified = false;
        let verifiedBy = '';
        let notVerified = false;
        let notVerifiedBy = '';

        if (data.verified) {
          verified = true;
          verifiedBy = userFromStorage.username || '';
          notVerified = false;
          notVerifiedBy = '';
        } else if (data.notVerified) {
          verified = false;
          verifiedBy = '';
          notVerified = true;
          notVerifiedBy = userFromStorage.username || '';
        }

        const filtered: any = {
          productType: data.productType,
          datumProizvodnje: data.datumProizvodnje,
          vremeProizvodnje: data.vremeProizvodnje,
          kontrolisao: id ? data.kontrolisao : (userFromStorage.username || ''),
          napomena: data.napomena ?? '',
          smena: data.smena,
          secena: (data as any).secena ?? '',
          pozicijaSecenja: (data as any).pozicijaSecenja ?? '',
          lambdaVrednost: data.lambdaVrednost != null ? Number(data.lambdaVrednost) : null,
          lambdaGustinga: data.lambdaGustinga != null ? Number(data.lambdaGustinga) : null,
          cs10: data.cs10 != null ? Number(data.cs10) : null,
          lambda9090: data.lambda9090 != null ? Number(data.lambda9090) : undefined,
          productLabel,
          datumTestiranja: data.datumTestiranja ?? '',
          // Fiksna polja - šalji null ako nema vrednosti
          masaRolne: data.masaRolne != null ? Number(data.masaRolne) : null,
          zapremina,
          gustina,
          m1: data.m1 != null ? Number(data.m1) : null,
          m2: data.m2 != null ? Number(data.m2) : null,
          m3: data.m3 != null ? Number(data.m3) : null,
          distribucija1: data.distribucija1 != null ? Number(data.distribucija1) : null,
          distribucija2: data.distribucija2 != null ? Number(data.distribucija2) : null,
          distribucija3: data.distribucija3 != null ? Number(data.distribucija3) : null,
          distribucija4: data.distribucija4 != null ? Number(data.distribucija4) : null,
          distribucija5: data.distribucija5 != null ? Number(data.distribucija5) : null,
          distribucija6: data.distribucija6 != null ? Number(data.distribucija6) : null,
          debljina1: data.debljina1 != null ? Number(data.debljina1) : null,
          debljinaUzorak1: data.debljinaUzorak1 != null ? Number(data.debljinaUzorak1) : null,
          debljinaUzorak2: data.debljinaUzorak2 != null ? Number(data.debljinaUzorak2) : null,
          debljinaUzorak3: data.debljinaUzorak3 != null ? Number(data.debljinaUzorak3) : null,
          pretvaranje: data.pretvaranje != null ? Number(data.pretvaranje) : 100, // read-only, default 100
          deltaP1: data.deltaP1 != null ? Number(data.deltaP1) : null,
          deltaP2: data.deltaP2 != null ? Number(data.deltaP2) : null,
          deltaP3: data.deltaP3 != null ? Number(data.deltaP3) : null,
          avgAFr,
          m4,
          procenatSM,
          avgDuzina: avgDuzinaVal,
          avgSirina: avgSirinaVal,
          avgDistribucija: avgDistribucijaVal,
          avgThickness: avgThicknessVal,
          actualThickness: actualThicknessVal,
          // Dinamička polja - uvek šalji, čak i ako su prazna
          duzineRows: Array.isArray(duzineRows) ? duzineRows.map(row => ({
            duzina: typeof row.duzina === 'number' ? row.duzina : Number(row.duzina) || 0,
            izmerena: typeof row.izmerena === 'number' ? row.izmerena : Number(row.izmerena) || 0
          })) : [],
          sirineRows: Array.isArray(sirineRows) ? sirineRows.map(row => ({
            sirina: typeof row.sirina === 'number' ? row.sirina : Number(row.sirina) || 0,
            izmerena: typeof row.izmerena === 'number' ? row.izmerena : Number(row.izmerena) || 0
          })) : [],
          rows: Array.isArray(rows) ? rows.map(row => ({
            ...row,
            measured: typeof row.measured === 'number' ? row.measured : (row.measured === '' ? '' : Number(row.measured) || ''),
            caliper: typeof row.caliper === 'number' ? row.caliper : (row.caliper === '' ? '' : Number(row.caliper) || '')
          })) : [],
          verified,
          verifiedBy,
          notVerified,
          notVerifiedBy,
        };
        // Ensure all model fields are present, even if not in the form or calculated
        const allFields = [
          'productType','datumProizvodnje','vremeProizvodnje','kontrolisao','masaRolne','zapremina','gustina','m1','m2','m3',
          'distribucija1','distribucija2','distribucija3','distribucija4','distribucija5','distribucija6','debljina1','debljinaUzorak1','debljinaUzorak2','debljinaUzorak3','pretvaranje','deltaP1','deltaP2','deltaP3','avgAFr','m4','procenatSM','avgDuzina','avgSirina','avgDistribucija','avgThickness','actualThickness','duzineRows','sirineRows','rows','productLabel','smena','datumTestiranja','napomena','secena','pozicijaSecenja','lambdaVrednost','lambdaGustinga','lambda9090','cs10', 'verified', 'verifiedBy', 'notVerified', 'notVerifiedBy'
        ];
        allFields.forEach(f => {
          if (!(f in filtered)) {
            // Set default: null for numbers, '' for strings, [] for arrays, undefined for lambda9090
            if ([
              'actualThickness','duzineRows','sirineRows','rows'
            ].includes(f)) filtered[f] = [];
            else if ([
              'productType','datumProizvodnje','vremeProizvodnje','kontrolisao','productLabel','smena','datumTestiranja','napomena','secena','pozicijaSecenja','avgDuzina','avgSirina','avgDistribucija','avgThickness'
            ].includes(f)) filtered[f] = '';
            else if (f === 'lambda9090') filtered[f] = undefined;
            else filtered[f] = null;
          }
        });
        // Koristi podatke iz forme, ne iz šablona
        // if (template?.duzine) filtered.duzineRows = duzineRows;
        // if (template?.sirine) filtered.sirineRows = sirineRows;
        // if (template?.debljine) filtered.rows = rows;
        // console.log('Šaljem na backend:', filtered);
        // console.log('[DEBUG] duzineRows pre slanja:', filtered.duzineRows);
        // console.log('[DEBUG] sirineRows pre slanja:', filtered.sirineRows);
        // console.log('[DEBUG] rows pre slanja:', filtered.rows);
        if (id) {
          await measurementsApi.update(id, filtered);
        } else {
          await measurementsApi.create(filtered);
        }
        
        // Završi edit sesiju nakon uspešnog čuvanja
        if (editSession.isEditing) {
          // console.log('🔍 Saving completed, ending session');
          await editSession.endSession();
        }
        
        navigate('/');
      } catch (err) {
        setError('Greška pri čuvanju podataka');
      } finally {
        setLoading(false);
      }
    };

    // Izračunavanje stvarne debljine za svaki red
    const actualThickness = useMemo(
      () => rows.map(row =>
        row.measured !== '' && row.caliper !== ''
          ? Number(row.measured) - Number(row.caliper)
          : ''
      ),
      [rows]
    );

    // Srednja vrednost stvarne debljine
    const avgThickness = useMemo(() => {
      const valid = actualThickness.filter(v => typeof v === 'number' && !isNaN(v)) as number[];
      if (valid.length === 0) return '';
      return (
        valid.reduce((acc, v) => (acc as number) + v, 0) / valid.length
      ).toFixed(2);
    }, [actualThickness]);

    // Izračunavanje srednje vrednosti dužine
    const avgDuzina = useMemo(() => {
      const { duzina200, duzina600, duzina1000 } = watchedValues;
      const values = [duzina200, duzina600, duzina1000].filter(v => v > 0);
      if (values.length === 0) return '';
      return (values.reduce((acc, v) => acc + v, 0) / values.length).toFixed(2);
    }, [watchedValues.duzina200, watchedValues.duzina600, watchedValues.duzina1000]);

    // Izračunavanje srednje vrednosti širine
    const avgSirina = useMemo(() => {
      const { sirina200, sirina1200, sirina2200, sirina3200, sirina4200, sirina5200 } = watchedValues;
      const values = [sirina200, sirina1200, sirina2200, sirina3200, sirina4200, sirina5200].filter(v => v > 0);
      if (values.length === 0) return '';
      return (values.reduce((acc, v) => acc + v, 0) / values.length).toFixed(2);
    }, [watchedValues.sirina200, watchedValues.sirina1200, watchedValues.sirina2200, watchedValues.sirina3200, watchedValues.sirina4200, watchedValues.sirina5200]);

    // Izračunavanje srednje vrednosti distribucije vlakana
    const avgDistribucija = useMemo(() => {
      const { distribucija1, distribucija2, distribucija3, distribucija4, distribucija5, distribucija6 } = watchedValues;
      const values = [distribucija1, distribucija2, distribucija3, distribucija4, distribucija5, distribucija6].filter(v => v > 0);
      if (values.length === 0) return '';
      return (values.reduce((acc, v) => acc + v, 0) / values.length).toFixed(2);
    }, [watchedValues.distribucija1, watchedValues.distribucija2, watchedValues.distribucija3, watchedValues.distribucija4, watchedValues.distribucija5, watchedValues.distribucija6]);

    // AFr kalkulacije
    const afrCalculations = useMemo(() => {
      const { deltaP1, deltaP2, deltaP3, debljina1, debljinaUzorak1, debljinaUzorak2, debljinaUzorak3, pretvaranje } = watchedValues;

      // NOVE FORMULE:
      // d = (debljina1 - debljinaUzorak) / pretvaranje
      // R = deltaP / 0.000005
      // A = 0.01
      // Rs = R * A
      // r = Rs / d
      // Afr = r / 1000

      const calc = (deltaP: number, debljinaUzorak: number) => {
        if (deltaP <= 0 || debljina1 <= 0 || debljinaUzorak <= 0 || pretvaranje <= 0) return null;
        const d = (debljina1 - debljinaUzorak) / pretvaranje;
        if (d === 0) return null;
        const R = deltaP / 0.000005;
        const A = 0.01;
        const Rs = R * A;
        const r = Rs / d;
        const Afr = r / 1000;
        return { d, R, A, Rs, r, Afr };
      };

      const afr1 = calc(deltaP1, debljinaUzorak1);
      const afr2 = calc(deltaP2, debljinaUzorak2);
      const afr3 = calc(deltaP3, debljinaUzorak3);

      const afrValues = [afr1?.Afr, afr2?.Afr, afr3?.Afr].filter(v => v !== null && v !== undefined) as number[];
      const avgAFr = afrValues.length > 0 ? afrValues.reduce((acc, v) => acc + v, 0) / afrValues.length : null;

      return { afr1, afr2, afr3, avgAFr };
    }, [watchedValues.deltaP1, watchedValues.deltaP2, watchedValues.deltaP3, watchedValues.debljina1, watchedValues.debljinaUzorak1, watchedValues.debljinaUzorak2, watchedValues.debljinaUzorak3, watchedValues.pretvaranje]);

    // Suva materija kalkulacije
    const suvaMaterijaCalculations = useMemo(() => {
      const { m1, m2, m3 } = watchedValues;
      if (m1 <= 0 || m2 <= 0 || m3 <= 0) return { m4: null, procenatSM: null };
      const m4 = m2 - m3;
      const procenatSM = ((m4 / m2) * 100);
      return { m4, procenatSM: Number(procenatSM.toFixed(2)) };
    }, [watchedValues.m1, watchedValues.m2, watchedValues.m3]);

    const handleRowChange = (idx: number, field: keyof PointRow, value: any) => {
      setRows(prev => {
        const updated = prev.map((row, i) =>
          i === idx ? { ...row, [field]: value === '' ? '' : Number(value) } : row
        );
        setValue('rows' as any, updated, { shouldDirty: true, shouldValidate: true });
        return updated;
      });
    };

    const handleTypeChange = (idx: number, newType: 'D' | 'L') => {
      // Find the next available preset for that type (by order)
      const typeIndex = rows.slice(0, idx + 1).filter(r => r.type === newType).length - 1;
      const preset = getPresetForType(newType, typeIndex);
      setRows(prev => prev.map((row, i) =>
        i === idx
          ? {
              ...row,
              type: newType,
              label: preset.label,
              length: preset.length,
              width: preset.width,
            }
          : row
      ));
    };

    const addRow = () => {
      setRows(prev => [
        ...prev,
        { type: 'D', label: `D${prev.filter(r => r.type === 'D').length + 1}`, length: 0, width: 0, measured: '', caliper: '' }
      ]);
    };

    const removeRow = (idx: number) => {
      setRows(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev);
    };

    // U ProductForm komponenti:
    const [duzineRows, setDuzineRows] = useState<DuzinaRow[]>([]);

    const handleDuzinaRowChange = (idx: number, field: keyof DuzinaRow, value: number | string) => {
      setDuzineRows(prev => {
        const updated = prev.map((row, i) => i === idx ? { ...row, [field]: value === '' ? '' : Number(value) } : row);
        setValue('duzineRows', updated);
        return updated;
      });
    };
    const addDuzinaRow = () => setDuzineRows(prev => {
      const updated = [...prev, { duzina: 0, izmerena: 0 }];
      setValue('duzineRows', updated);
      return updated;
    });
    const removeDuzinaRow = (idx: number) => setDuzineRows(prev => {
      const updated = prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev;
      setValue('duzineRows', updated);
      return updated;
    });

    const avgDuzinaDynamic = useMemo(() => {
      const valid = duzineRows.map(r => r.izmerena).filter(v => v > 0);
      if (valid.length === 0) return '';
      return (valid.reduce((acc, v) => acc + v, 0) / valid.length).toFixed(2);
    }, [duzineRows]);

    // U ProductForm komponenti:
    const [sirineRows, setSirineRows] = useState<SirinaRow[]>([]);

    const handleSirinaRowChange = (idx: number, field: keyof SirinaRow, value: number | string) => {
      setSirineRows(prev => {
        const updated = prev.map((row, i) => i === idx ? { ...row, [field]: value === '' ? '' : Number(value) } : row);
        setValue('sirineRows', updated);
        return updated;
      });
    };
    const addSirinaRow = () => setSirineRows(prev => {
      const updated = [...prev, { sirina: 0, izmerena: 0 }];
      setValue('sirineRows', updated);
      return updated;
    });
    const removeSirinaRow = (idx: number) => setSirineRows(prev => {
      const updated = prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev;
      setValue('sirineRows', updated);
      return updated;
    });

    const avgSirinaDynamic = useMemo(() => {
      const valid = sirineRows.map(r => r.izmerena).filter(v => v > 0);
      if (valid.length === 0) return '';
      return (valid.reduce((acc, v) => acc + v, 0) / valid.length).toFixed(2);
    }, [sirineRows]);

    // Dodaj useMemo za automatski izračunatu zapreminu
    const autoZapremina = useMemo(() => {
      const duzina = parseFloat(avgDuzinaDynamic);
      const sirina = parseFloat(avgSirinaDynamic);
      const debljina = parseFloat(avgThickness);
      if (isNaN(duzina) || isNaN(sirina) || isNaN(debljina)) return '';
      return ((duzina * sirina * debljina) / 1_000_000_000).toFixed(3);
    }, [avgDuzinaDynamic, avgSirinaDynamic, avgThickness]);

    // Ukloniću višak deklaracija 'selectedProduct' i koristiću samo jednu, odmah posle useForm
    const selectedProduct = useMemo(() => {
      return productOptions.find(p => p.name === watch('productType'));
    }, [productOptions, watch('productType')]);

    const lambdaRefValue = useMemo(() => {
      if (selectedProduct && selectedProduct.lambdaGroup) {
        const match = selectedProduct.lambdaGroup.match(/(\d+)/);
        if (match) {
          const groupNum = parseFloat(match[1]);
          if (!isNaN(groupNum)) return groupNum - 1;
        }
      }
      return null;
    }, [selectedProduct]);

    // Pomeri selectedProductForTemplate iznad svih useEffect hookova gde se koristi
    const selectedProductForTemplate = useMemo(() => {
      if (templateMode && templateProductId && productOptions.length > 0) {
        return productOptions.find(p => p._id === templateProductId);
      }
      if (!templateMode && watch('productType') && productOptions.length > 0) {
        return productOptions.find(p => p.name === watch('productType'));
      }
      return undefined;
    }, [templateMode, templateProductId, productOptions, watch('productType')]);

    // Definiši helper funkcije van komponente
    function getLambdaInputColor(val: any, lambdaRefValue: number | null) {
      if (val === '' || val === null || isNaN(Number(val)) || Number(val) === 0) return '#fff9c4';
      if (lambdaRefValue === null) return '#fff9c4';
      const numVal = Number(val);
      if (isNaN(numVal)) return '#fff9c4';
      // OBRNUTO: zeleno ako je manja ili jednaka, crveno ako je veća
      return numVal <= lambdaRefValue ? '#c8e6c9' : '#ffcdd2';
    }
    function getCs10InputColor(val: any, csToleranceValue: number) {
      if (val === '' || val === null || isNaN(Number(val)) || Number(val) === 0) return '#fff9c4';
      if (typeof csToleranceValue !== 'number' || isNaN(csToleranceValue)) return '#fff9c4';
      const numVal = Number(val);
      if (isNaN(numVal)) return '#fff9c4';
      return numVal > csToleranceValue ? '#c8e6c9' : '#ffcdd2';
    }

    // Zapremina proizvoda koristi već izračunatu automatsku zapreminu na osnovu dinamičkih srednjih vrednosti
    const zapreminaProizvoda = autoZapremina;

    // Izračunaj gustinu na osnovu mase i zapremine
    const gustinaProizvoda = useMemo(() => {
      const masa = Number(watch('masaRolne'));
      const zapremina = Number(zapreminaProizvoda);
      if (!masa || !zapremina) return '';
      return (masa / zapremina).toFixed(2);
    }, [watch('masaRolne'), zapreminaProizvoda]);

    // U ProductForm komponenti:
    const [rowTemplates, setRowTemplates] = useState<{ [productId: string]: { type: string; label: string; value: string }[] }>({});

    // Učitaj šablone iz localStorage na mount
    useEffect(() => {
      const saved = localStorage.getItem('rowTemplates');
      let parsed: Record<string, any> = {};
      if (saved) {
        try {
          parsed = JSON.parse(saved) as Record<string, any>;
        } catch {}
      }
      setRowTemplates(parsed);
    }, []);
    // Kada admin sačuva šablon u ProductAdmin, neka se pozove localStorage.setItem('rowTemplates', JSON.stringify(rowTemplates));

    // Kada se izabere proizvod, koristi njegov šablon za inicijalizaciju redova
    useEffect(() => {
      const selected = productOptions.find(p => p.name === watch('productType'));
      if (selected && rowTemplates[selected._id]) {
        // Pretvori šablon u rows
        setRows(rowTemplates[selected._id].map(t => ({
          type: t.type === 'duzina' ? 'D' : 'L',
          label: t.label,
          length: t.type === 'duzina' ? Number(t.value) : 0,
          width: t.type === 'sirina' ? Number(t.value) : 0,
          measured: '',
          caliper: ''
        }) as PointRow));
      }
    }, [watch('productType'), productOptions, rowTemplates]);

    // Ako je templateMode, koristi templateProductId za šablon
    useEffect(() => {
      if (templateMode && templateProductId) {
        templatesApi.getByProductId(templateProductId)
          .then(template => {
            setTemplateDuzina(Array.isArray(template?.duzine) && template.duzine.length > 0
              ? template.duzine
              : [{ duzina: 0 }]
            );
            setTemplateSirina(Array.isArray(template?.sirine) && template.sirine.length > 0
              ? template.sirine
              : [{ sirina: 0 }]
            );
            setTemplateDebljina(Array.isArray(template?.debljine) && template.debljine.length > 0
              ? template.debljine.map((r: any, i: number) => ({
                  type: r.label && r.label.startsWith('D') ? 'D' as 'D' : 'L' as 'L',
                  label: r.label || `D${i + 1}`,
                  length: typeof r.length === 'number' ? r.length : 0,
                  width: typeof r.width === 'number' ? r.width : 0,
                  measured: r.measured ?? '',
                  caliper: r.caliper ?? ''
                }))
              : [{ type: 'D' as 'D', label: 'D1', length: 0, width: 0, measured: '', caliper: '' }]
            );
            if (template?.densityRange) setDensityRange(template.densityRange);
            if (template?.smRange) setSmRange(template.smRange);
            if (template?.lengthRange) setLengthRange(template.lengthRange);
            if (template?.widthRange) setWidthRange(template.widthRange);
            if (template?.fiberDistRange) setFiberDistRange(template.fiberDistRange);
            if (template?.thicknessRange) setThicknessRange(template.thicknessRange);
            if (template?.afrRange) setAfrRange(template.afrRange);
          })
          .catch(async (err) => {
            if (err?.response?.status === 404 && templateProductId) {
              await createEmptyTemplate(templateProductId);
              const template = await templatesApi.getByProductId(templateProductId);
              if (template) {
                if (Array.isArray(template.duzine) && template.duzine.length > 0) {
                  const duzineArr = template.duzine.map((d: any) => ({ duzina: typeof d.duzina === 'number' ? d.duzina : 0, izmerena: 0 }));
                  setTemplateDuzina(duzineArr);
                }
                if (Array.isArray(template.sirine) && template.sirine.length > 0) {
                  const sirineArr = template.sirine.map((s: any) => ({ sirina: typeof s.sirina === 'number' ? s.sirina : 0, izmerena: 0 }));
                  setTemplateSirina(sirineArr);
                }
                if (Array.isArray(template.debljine) && template.debljine.length > 0) {
                  const debljineArr = template.debljine.map((d: any, i: number) => ({
                    type: d.label && d.label.startsWith('D') ? 'D' as 'D' : 'L' as 'L',
                    label: d.label || `D${i + 1}`,
                    length: typeof d.length === 'number' ? d.length : 0,
                    width: typeof d.width === 'number' ? d.width : 0,
                    measured: d.measured ?? '',
                    caliper: d.caliper ?? ''
                  }));
                  setTemplateDebljina(debljineArr);
                }
              }
            } else {
              setTemplateDuzina([{ duzina: 0 }]);
              setTemplateSirina([{ sirina: 0 }]);
              setTemplateDebljina([{ type: 'D' as 'D', label: 'D1', length: 0, width: 0, measured: '', caliper: '' }]);
            }
          });
      }
    }, [templateMode, templateProductId]);

    // useEffect za tehnologe (novi unos)
    useEffect(() => {
      if (!templateMode && !id) {
        const selected = productOptions.find(p => p.name === watch('productType'));
        if (selected) {
          templatesApi.getByProductId(selected._id)
            .then(template => {
              if (template) {
                // Popuni duzineRows
                if (Array.isArray(template.duzine) && template.duzine.length > 0) {
                  const duzineArr = template.duzine.map((d: any) => ({ duzina: typeof d.duzina === 'number' ? d.duzina : 0, izmerena: 0 }));
                  setDuzineRows(duzineArr);
                  // console.log('duzineRows postavljen:', duzineArr);
                }
                // Popuni sirineRows
                if (Array.isArray(template.sirine) && template.sirine.length > 0) {
                  const sirineArr = template.sirine.map((s: any) => ({ sirina: typeof s.sirina === 'number' ? s.sirina : 0, izmerena: 0 }));
                  setSirineRows(sirineArr);
                  // console.log('sirineRows postavljen:', sirineArr);
                }
                // Popuni rows (debljina)
                if (Array.isArray(template.debljine) && template.debljine.length > 0) {
                  const rowsArr = template.debljine.map((d: any) => ({
                    type: d.label && d.label.startsWith('D') ? 'D' as 'D' : 'L' as 'L',
                    label: d.label || '',
                    length: typeof d.length === 'number' ? d.length : 0,
                    width: typeof d.width === 'number' ? d.width : 0,
                    measured: '',
                    caliper: ''
                  }));
                  setRows(rowsArr);
                }
                // DODATO: Postavi min/max opsege iz šablona
                if (template.densityRange) setDensityRange(template.densityRange);
                if (template.smRange) setSmRange(template.smRange);
                if (template.lengthRange) setLengthRange(template.lengthRange);
                if (template.widthRange) setWidthRange(template.widthRange);
                if (template.fiberDistRange) setFiberDistRange(template.fiberDistRange);
                if (template.thicknessRange) setThicknessRange(template.thicknessRange);
                if (template.afrRange) setAfrRange(template.afrRange);
              }
            })
            .catch(async (err) => {
              if (err?.response?.status === 404 && selected && selected._id) {
                await createEmptyTemplate(selected._id);
                const template = await templatesApi.getByProductId(selected._id);
                if (template) {
                  if (Array.isArray(template.duzine) && template.duzine.length > 0) {
                    const duzineArr = template.duzine.map((d: any) => ({ duzina: typeof d.duzina === 'number' ? d.duzina : 0, izmerena: 0 }));
                    setDuzineRows(duzineArr);
                  }
                  if (Array.isArray(template.sirine) && template.sirine.length > 0) {
                    const sirineArr = template.sirine.map((s: any) => ({ sirina: typeof s.sirina === 'number' ? s.sirina : 0, izmerena: 0 }));
                    setSirineRows(sirineArr);
                  }
                  if (Array.isArray(template.debljine) && template.debljine.length > 0) {
                    const rowsArr = template.debljine.map((d: any) => ({
                      type: d.label && d.label.startsWith('D') ? 'D' as 'D' : 'L' as 'L',
                      label: d.label || '',
                      length: typeof d.length === 'number' ? d.length : 0,
                      width: typeof d.width === 'number' ? d.width : 0,
                      measured: '',
                      caliper: ''
                    }));
                    setRows(rowsArr);
                  }
                }
              } else {
                setDuzineRows([{ duzina: 0, izmerena: 0 }]);
                setSirineRows([{ sirina: 0, izmerena: 0 }]);
                setRows([{ type: 'D' as 'D', label: 'D1', length: 0, width: 0, measured: '', caliper: '' }]);
              }
            });
        }
      }
    }, [watch('productType'), productOptions, templateMode, id]);

    // Default vrednosti za šablon
    const getDefaultTemplateDuzine = () => [{ duzina: 0 }];
    const getDefaultTemplateSirine = () => [{ sirina: 0 }];
    const getDefaultTemplateDebljina = () => [{ type: 'D' as 'D', label: 'D1', length: 0, width: 0, measured: 0, caliper: 0 }];

    // Dodaj state-ove za template sekcije
    const [templateDebljina, setTemplateDebljina] = useState<PointRow[]>(getDefaultTemplateDebljina());
    const [templateDuzina, setTemplateDuzina] = useState<{ duzina: number }[]>([{ duzina: 0 }]);
    const [templateSirina, setTemplateSirina] = useState<{ sirina: number }[]>([{ sirina: 0 }]);

    // Dodaj state za min/max opsege za template režim
    const [densityRange, setDensityRange] = useState<{ min: number; max: number }>({ min: 0, max: 0 });
    const [smRange, setSmRange] = useState<{ min: number; max: number }>({ min: 0, max: 0 });
    const [lengthRange, setLengthRange] = useState<{ min: number; max: number }>({ min: 0, max: 0 });
    const [widthRange, setWidthRange] = useState<{ min: number; max: number }>({ min: 0, max: 0 });
    const [fiberDistRange, setFiberDistRange] = useState<{ min: number; max: number }>({ min: 0, max: 0 });
    const [thicknessRange, setThicknessRange] = useState<{ min: number; max: number }>({ min: 0, max: 0 });
    const [afrRange, setAfrRange] = useState<{ min: number }>({ min: 1 });

    // Dodaj state za tolerancije
    const [lambdaToleranceValue, setLambdaToleranceValue] = useState<number | null>(null);
    const [csToleranceValue, setCsToleranceValue] = useState<number>(0.20);

    // Kada admin otvori šablon, automatski popuni tolerancije na osnovu proizvoda
    useEffect(() => {
      if (templateMode && selectedProductForTemplate) {
        // lambda tolerancija
        const group = selectedProductForTemplate.lambdaGroup;
        if (group) {
          const num = parseFloat(group.replace(/[^0-9.]/g, ''));
          if (!isNaN(num)) setLambdaToleranceValue(num - 1);
        }
        // cs tolerancija
        setCsToleranceValue(0.20);
      }
    }, [templateMode, selectedProductForTemplate]);

    // Kada se učita šablon iz baze, popuni tolerancije ako postoje
    useEffect(() => {
      if (templateMode && templateProductId) {
        templatesApi.getByProductId(templateProductId)
          .then(template => {
            if (template?.lambdaTolerance !== undefined && template.lambdaTolerance !== null) setLambdaToleranceValue(template.lambdaTolerance);
            if (template?.csTolerance !== undefined && template.csTolerance !== null) setCsToleranceValue(template.csTolerance);
          });
      }
    }, [templateMode, templateProductId]);

    // Na 'Sačuvaj šablon' (npr. dugme), upiši strukturu u localStorage
    const handleSaveTemplate = async () => {
      if (templateMode && templateProductId) {
        // Filtriraj debljine da ukloniš type polje jer se ne čuva u bazi
        const debljineForSave = templateDebljina.map(({ type, ...rest }) => rest);
        
        await templatesApi.saveTemplate(templateProductId, {
          duzine: templateDuzina,
          sirine: templateSirina,
          debljine: debljineForSave,
          densityRange,
          smRange,
          lengthRange,
          widthRange,
          fiberDistRange,
          thicknessRange,
          afrRange,
          lambdaTolerance: lambdaToleranceValue,
          csTolerance: csToleranceValue,
        });
        if (onTemplateSave) onTemplateSave();
      }
    };

    // Funkcije za automatsko generisanje labela
    const handleAddDebljina = () => {
      const dCount = templateDebljina.filter(r => r.label.startsWith('D')).length;
      const lCount = templateDebljina.filter(r => r.label.startsWith('L')).length;
      const nextLabel = dCount === lCount ? `D${dCount + 1}` : `L${lCount + 1}`;
      setTemplateDebljina(prev => [
        ...prev,
        {
          type: nextLabel.startsWith('D') ? 'D' as 'D' : 'L' as 'L',
          label: nextLabel,
          length: 0,
          width: 0,
          measured: 0,
          caliper: 0
        }
      ]);
    };
    const handleAddDuzina = () => {
      const nextDuzina = { duzina: 0 };
      setTemplateDuzina(prev => [...prev, nextDuzina]);
    };
    const handleAddSirina = () => {
      const nextSirina = { sirina: 0 };
      setTemplateSirina(prev => [...prev, nextSirina]);
    };

    // Dodaj useEffect za automatsko popunjavanje redova prema šablonu iz baze kada tehnolog izabere proizvod
    useEffect(() => {
      // Samo ako nismo u template režimu i nema postojećeg ID-a (tj. kada tehnolog unosi novo merenje)
      if (!templateMode && !id) {
        const selected = productOptions.find(p => p.name === watch('productType'));
        if (selected) {
          templatesApi.getByProductId(selected._id)
            .then(template => {
              if (template) {
                // Popuni duzineRows
                if (Array.isArray(template.duzine) && template.duzine.length > 0) {
                  const duzineArr = template.duzine.map((d: any) => ({ duzina: typeof d.duzina === 'number' ? d.duzina : 0, izmerena: 0 }));
                  setDuzineRows(duzineArr);
                  // console.log('duzineRows postavljen:', duzineArr);
                }
                // Popuni sirineRows
                if (Array.isArray(template.sirine) && template.sirine.length > 0) {
                  const sirineArr = template.sirine.map((s: any) => ({ sirina: typeof s.sirina === 'number' ? s.sirina : 0, izmerena: 0 }));
                  setSirineRows(sirineArr);
                  // console.log('sirineRows postavljen:', sirineArr);
                }
                // Popuni rows (debljina)
                if (Array.isArray(template.debljine) && template.debljine.length > 0) {
                  const rowsArr = template.debljine.map((d: any) => ({
                    type: d.label && d.label.startsWith('D') ? 'D' as 'D' : 'L' as 'L',
                    label: d.label || '',
                    length: typeof d.length === 'number' ? d.length : 0,
                    width: typeof d.width === 'number' ? d.width : 0,
                    measured: '',
                    caliper: ''
                  }));
                  setRows(rowsArr);
                }
                // DODATO: Postavi min/max opsege iz šablona
                if (template.densityRange) setDensityRange(template.densityRange);
                if (template.smRange) setSmRange(template.smRange);
                if (template.lengthRange) setLengthRange(template.lengthRange);
                if (template.widthRange) setWidthRange(template.widthRange);
                if (template.fiberDistRange) setFiberDistRange(template.fiberDistRange);
                if (template.thicknessRange) setThicknessRange(template.thicknessRange);
                if (template.afrRange) setAfrRange(template.afrRange);
              }
            })
            .catch(async (err) => {
              if (err?.response?.status === 404 && selected && selected._id) {
                await createEmptyTemplate(selected._id);
                const template = await templatesApi.getByProductId(selected._id);
                if (template) {
                  if (Array.isArray(template.duzine) && template.duzine.length > 0) {
                    const duzineArr = template.duzine.map((d: any) => ({ duzina: typeof d.duzina === 'number' ? d.duzina : 0, izmerena: 0 }));
                    setDuzineRows(duzineArr);
                  }
                  if (Array.isArray(template.sirine) && template.sirine.length > 0) {
                    const sirineArr = template.sirine.map((s: any) => ({ sirina: typeof s.sirina === 'number' ? s.sirina : 0, izmerena: 0 }));
                    setSirineRows(sirineArr);
                  }
                  if (Array.isArray(template.debljine) && template.debljine.length > 0) {
                    const rowsArr = template.debljine.map((d: any) => ({
                      type: d.label && d.label.startsWith('D') ? 'D' as 'D' : 'L' as 'L',
                      label: d.label || '',
                      length: typeof d.length === 'number' ? d.length : 0,
                      width: typeof d.width === 'number' ? d.width : 0,
                      measured: '',
                      caliper: ''
                    }));
                    setRows(rowsArr);
                  }
                }
              } else {
                setDuzineRows([{ duzina: 0, izmerena: 0 }]);
                setSirineRows([{ sirina: 0, izmerena: 0 }]);
                setRows([{ type: 'D' as 'D', label: 'D1', length: 0, width: 0, measured: '', caliper: '' }]);
              }
            });
        }
      }
    }, [watch('productType'), productOptions, templateMode, id]);

    // Omogući parent komponenti da pozove handleSaveTemplate
    useImperativeHandle(ref, () => ({
      handleSaveTemplate,
    }));

    // Nakon useForm, dodaj useEffect za inicijalizaciju smene i secena:
    useEffect(() => {
      if (!id && !watchedValues.smena) {
        setValue('smena', getSmena());
      }
      if (!id && !watchedValues.secena) {
        setValue('secena', 'bez_secena');
      }
    }, [id, setValue, watchedValues.smena, watchedValues.secena]);

    useEffect(() => {
      if (watch('secena') !== 'secena') {
        setValue('pozicijaSecenja', '');
      }
    }, [watch('secena'), setValue]);

    // Helper funkcija za proveru opsega
    const inRange = (value: number | string | null | undefined, range?: { min: number; max?: number }) => {
      if (value === null || value === undefined || !range) return undefined;
      let numValue: number;
      if (typeof value === 'string') {
        numValue = Number(value);
        if (isNaN(numValue)) return undefined;
      } else {
        numValue = value;
      }
      if (range.max !== undefined) {
        return numValue >= range.min && numValue <= range.max;
      } else {
        return numValue >= range.min;
      }
    };

    // Helper za lambda toleranciju
    const lambdaTolerance = (lambdaGroup: string | undefined) => {
      if (!lambdaGroup) return undefined;
      const num = parseFloat(lambdaGroup.replace(/[^0-9.]/g, ''));
      if (isNaN(num)) return undefined;
      return num - 1;
    };

    // Automatski izračunaj lambdaVrednost na osnovu lambda grupe proizvoda
    // useEffect(() => {
    //   const selected = productOptions.find(p => p.name === watch('productType'));
    //   if (selected && selected.lambdaGroup) {
    //     // Izvuci broj iz lambda grupe
    //     const match = selected.lambdaGroup.match(/(\d+)/);
    //     if (match) {
    //       const groupNum = parseFloat(match[1]);
    //       if (!isNaN(groupNum)) {
    //         setValue('lambdaVrednost', groupNum - 1);
    //       }
    //     }
    //   }
    // }, [watch('productType'), productOptions, setValue]);

    // Helper funkcija: svi rezultati u zelenom
    const sviRezultatiZeleni = () => {
      // Proveri sve prosečne vrednosti koje imaju opseg (gustina, %SM, duzina, sirina, distribucija, debljina, AFr srednja)
      const checks = [
        inRange(Number(gustinaProizvoda), densityRange),
        inRange(suvaMaterijaCalculations.procenatSM, smRange),
        inRange(Number(avgDuzinaDynamic), lengthRange),
        inRange(Number(avgSirinaDynamic), widthRange),
        inRange(Number(avgDistribucija), fiberDistRange),
        inRange(Number(avgThickness), thicknessRange),
        inRange(Number(afrCalculations.avgAFr), { min: afrMin })
      ];

      // Ako je bilo koji undefined (nema opsega ili vrednosti), ignoriši ga
      return checks.filter(x => x !== undefined).every(x => x === true);
    };

    // Helper funkcija: bar jedan rezultat u crvenom
    const barJedanCrveni = () => {
      const checks = [
        inRange(Number(gustinaProizvoda), densityRange),
        inRange(suvaMaterijaCalculations.procenatSM, smRange),
        inRange(Number(avgDuzinaDynamic), lengthRange),
        inRange(Number(avgSirinaDynamic), widthRange),
        inRange(Number(avgDistribucija), fiberDistRange),
        inRange(Number(avgThickness), thicknessRange),
        inRange(Number(afrCalculations.avgAFr), { min: afrMin })
      ];

      // Ako je bilo koji false, postoji crveno
      return checks.filter(x => x !== undefined).some(x => x === false);
    };

    const location = useLocation();
    const isKontrola = new URLSearchParams(location.search).get('kontrola') === '1';

    // Dodaj useEffect za admina: uvek učitaj opsege iz šablona kada edituje/verifikuje
    useEffect(() => {
      if (!templateMode && id && ((role === 'admin' || role === 'master_admin') || role === 'tehnolog')) {
        // Učitaj podatke iz baze za merenje
        measurementsApi.getById(id)
          .then(data => {
            // Pronađi proizvod na osnovu productType
            const selected = productOptions.find(p => p.name === data.productType);
            if (selected) {
              templatesApi.getByProductId(selected._id)
                .then(template => {
                  if (template?.densityRange) setDensityRange(template.densityRange);
                  if (template?.smRange) setSmRange(template.smRange);
                  if (template?.lengthRange) setLengthRange(template.lengthRange);
                  if (template?.widthRange) setWidthRange(template.widthRange);
                  if (template?.fiberDistRange) setFiberDistRange(template.fiberDistRange);
                  if (template?.thicknessRange) setThicknessRange(template.thicknessRange);
                  if (template?.afrRange) setAfrRange(template.afrRange);
                });
            }
          });
      }
    }, [templateMode, id, role, productOptions]);

    // Helper for admin rounding
    const formatAdminNumber = (value: any) =>
      value !== '' && value !== undefined && !isNaN(Number(value))
        ? Number(value).toFixed(2)
        : value;

    // Helper za automatsko kreiranje praznog šablona
    const createEmptyTemplate = async (productId: string) => {
      try {
        await templatesApi.saveTemplate(productId, {
          duzine: [{ duzina: 0 }],
          sirine: [{ sirina: 0 }],
          debljine: [{ label: 'D1', length: 0, width: 0, measured: '', caliper: '' }],
          densityRange: { min: 0, max: 0 },
          smRange: { min: 0, max: 0 },
          lengthRange: { min: 0, max: 0 },
          widthRange: { min: 0, max: 0 },
          fiberDistRange: { min: 0, max: 0 },
          thicknessRange: { min: 0, max: 0 },
          afrRange: { min: 0, max: 0 },
          lambdaTolerance: 0,
          csTolerance: 0.2
        });
        setError('Automatski je kreiran prazan šablon za ovaj proizvod.');
      } catch (e) {
        setError('Greška pri automatskom kreiranju šablona.');
      }
    };

    // Da li je editovanje zaključano za tehnologa (ako je status promenjen)
    const isLockedForTehnolog = role === 'tehnolog' && (watchedValues.verified || watchedValues.notVerified);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);

    // Funkcija za ručno učitavanje šablona
    const handleLoadTemplate = async () => {
      if (!selectedProduct) return;
      try {
        const template = await templatesApi.getByProductId(selectedProduct._id);
        // Dužine
        if (Array.isArray(template.duzine) && template.duzine.length > 0) {
          const duzineArr = template.duzine.map((d: any) => ({ duzina: typeof d.duzina === 'number' ? d.duzina : 0, izmerena: '' }));
          setDuzineRows(duzineArr);
          setValue('duzineRows', duzineArr);
        }
        // Širine
        if (Array.isArray(template.sirine) && template.sirine.length > 0) {
          const sirineArr = template.sirine.map((s: any) => ({ sirina: typeof s.sirina === 'number' ? s.sirina : 0, izmerena: '' }));
          setSirineRows(sirineArr);
          setValue('sirineRows', sirineArr);
        }
        // Debljine
        if (Array.isArray(template.debljine) && template.debljine.length > 0) {
          const rowsArr = template.debljine.map((d: any, i: number) => ({
            type: d.label && d.label.startsWith('D') ? 'D' : 'L',
            label: d.label || `D${i + 1}`,
            length: typeof d.length === 'number' ? d.length : 0,
            width: typeof d.width === 'number' ? d.width : 0,
            measured: '',
            caliper: ''
          }));
          setRows(rowsArr);
        }
        setSnackbarOpen(true);
      } catch (e) {
        setError('Greška pri učitavanju šablona');
      }
    };

    const isDirtyRef = useRef(false);

    // Prati promene u formi i postavlja dirty flag
    useEffect(() => {
      const subscription = watch(() => {
        isDirtyRef.current = true;
      });
      return () => subscription.unsubscribe();
    }, [watch]);

    // Upozorenje pri izlasku iz taba/refresh
    useEffect(() => {
      const handleBeforeUnload = (e: BeforeUnloadEvent) => {
        // Završi sesiju ako postoji
        if (editSession.isEditing && editSession.sessionId) {
          // console.log('🔍 Page unload, ending session');
          // Pošalji sync request da završi sesiju
          navigator.sendBeacon('/api/active-sessions/end-beacon', JSON.stringify({
            sessionId: editSession.sessionId
          }));
        }
        
        if (isDirtyRef.current) {
          e.preventDefault();
          e.returnValue = '';
        }
      };
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [editSession.isEditing, editSession.sessionId]);

    // Funkcija za proveru i potvrdu izlaska
    const handleBack = async () => {
      // Završi edit sesiju ako postoji
      if (editSession.isEditing) {
        // console.log('🔍 Back button clicked, ending session');
        await editSession.endSession();
      }
      
      if (isDirtyRef.current) {
        if (window.confirm('Imate nečuvane izmene. Da li ste sigurni da želite da izađete?')) {
          navigate('/');
        }
      } else {
        navigate('/');
      }
    };

    const productNameFilter = createFilterOptions({
      matchFrom: 'any',
      stringify: (option) => (option as Product).name,
    });

    // Dodaj state za sakrivanje AFr detaljnih polja
    const [hideAfrDetails, setHideAfrDetails] = useState(false);
  const [hideReferentnaVrednost, setHideReferentnaVrednost] = useState(false);
    useEffect(() => {
      const fetchSettings = async () => {
        try {
          const res = await formSettingsApi.getFormSettings();
          if (res) {
            setHideAfrDetails(res.hideAfrDetails || false);
            setHideReferentnaVrednost(res.hideReferentnaVrednost || false);
          }
        } catch (e) {
          // ignore
        }
      };
      
      // Učitaj podešavanja odmah
      fetchSettings();
      
      // Proveri podešavanja svakih 5 sekundi za sve korisnike
      const interval = setInterval(fetchSettings, 5000);
      
      // Osvežavanje kada se prozor fokusira (kada se vrati iz Settings)
      const handleFocus = () => {
        fetchSettings();
      };
      
      window.addEventListener('focus', handleFocus);
      
      return () => {
        if (interval) clearInterval(interval);
        window.removeEventListener('focus', handleFocus);
      };
    }, [role]);

    // Fallback za minimalnu vrednost AFr
    const afrMin = afrRange && typeof afrRange.min === 'number' && afrRange.min >= 1 ? afrRange.min : 1;

    // return JSX kao i ranije
    if (loading && id) {
      return (
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        </Container>
      );
    }

    // Dodaj proveru da li su podaci učitani
    if (id && duzineRows.length === 0 && sirineRows.length === 0 && rows.length === 0) {
      return (
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Učitavanje podataka...</Typography>
          </Box>
        </Container>
      );
    }

    // console.log('[DEBUG] Render: id=', id, 'rows=', rows, 'duzineRows=', duzineRows, 'sirineRows=', sirineRows, 'loading=', loading);

    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            sx={{ mb: 2 }}
          >
            Nazad
          </Button>
          <Typography variant="h4" component="h1" gutterBottom>
            {id ? 'Izmeni merenje' : 'Novo merenje'}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Unesite podatke o tehničkom merenju proizvoda
          </Typography>
          {editSession.isEditing && (
            <Box sx={{ mt: 1 }}>
              <Chip 
                label="U edit modu" 
                color="success" 
                size="small"
                sx={{ mr: 1 }}
              />
              <Typography variant="caption" color="text.secondary">
                Možete bezbedno editujati ovaj proizvod
              </Typography>
            </Box>
          )}
        </Box>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        <form onSubmit={handleSubmit(onSubmit)} onKeyDown={e => { if (e.key === 'Enter') e.preventDefault(); }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {(!templateMode || templateMode) && (
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Osnovni podaci
                </Typography>
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: 2
                }}>
                  {/* Prikaži samo Tip proizvoda u template režimu */}
                  <Box>
                    {templateMode ? (
                      <TextField
                        fullWidth
                        label="Tip proizvoda"
                        value={selectedProductForTemplate ? `${selectedProductForTemplate.name} - ${selectedProductForTemplate.width}x${selectedProductForTemplate.thickness}x${selectedProductForTemplate.length} mm` : ''}
                        InputProps={{ readOnly: true }}
                        sx={{ bgcolor: '#fff9c4' }}
                        
                      />
                    ) : (
                      <Controller
                        name="productType"
                        control={control}
                        render={({ field }) => (
                          <Autocomplete
                            options={productOptions}
                            getOptionLabel={option => `${(option as Product).name} - ${(option as Product).width}x${(option as Product).thickness}x${(option as Product).length} mm`}
                            value={productOptions.find(p => p.name === field.value) || null}
                            onChange={(_, newValue) => field.onChange(newValue ? (newValue as Product).name : '')}
                            renderInput={params => (
                              <TextField {...params} label="Tip proizvoda" sx={{ bgcolor: '#fff9c4' }} />
                            )}
                            ListboxProps={{
                              style: { maxHeight: 48 * 10, overflowY: 'auto' } // 10 proizvoda po 48px visine
                            }}
                            isOptionEqualToValue={(option, value) => (option as Product).name === (value as Product).name}
                            filterOptions={productNameFilter}
                          />
                        )}
                      />
                    )}
                  </Box>
                  {/* Sakrij ostala polja u template režimu */}
                  {!templateMode && (
                    <>
                      <Box>
                        <Controller
                          name="datumProizvodnje"
                          control={control}
                          render={({ field }) => (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                label="Datum proizvodnje"
                                value={field.value ? dayjs(field.value) : null}
                                onChange={val => field.onChange(dayjs.isDayjs(val) ? val.format('YYYY-MM-DD') : '')}
                                maxDate={dayjs()}
                                slotProps={{ textField: { fullWidth: true, sx: { bgcolor: '#fff9c4' } } }}
                              />
                            </LocalizationProvider>
                          )}
                        />
                      </Box>
                      <Box>
                        <Controller
                          name="vremeProizvodnje"
                          control={control}
                          render={({ field }) => (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <TimePicker
                                label="Vreme proizvodnje"
                                value={field.value ? dayjs(field.value, 'HH:mm') : null}
                                onChange={val => field.onChange(dayjs.isDayjs(val) ? val.format('HH:mm') : '')}
                                ampm={false}
                                timeSteps={{ minutes: 1 }}
                                slotProps={{ textField: { fullWidth: true, sx: { bgcolor: '#fff9c4' } } }}
                              />
                            </LocalizationProvider>
                          )}
                        />
                      </Box>
                      <Box>
                        <Controller
                          name="kontrolisao"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Merio"
                              InputProps={{ readOnly: true }}
                            />
                          )}
                        />
                      </Box>
                      <Box>
                        <Controller
                          name="smena"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Smena"
                              InputProps={{ readOnly: true }}
                            />
                          )}
                        />
                      </Box>
                      <Box>
                        <Controller
                          name="datumTestiranja"
                          control={control}
                          render={({ field }) => (
                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                              <DatePicker
                                label="Datum testiranja"
                                value={field.value ? dayjs(field.value) : null}
                                onChange={val => field.onChange(dayjs.isDayjs(val) ? val.format('YYYY-MM-DD') : '')}
                                maxDate={dayjs()}
                                slotProps={{ textField: { fullWidth: true, sx: { bgcolor: '#fff9c4' } } }}
                              />
                            </LocalizationProvider>
                          )}
                        />
                      </Box>
                      <Box>
                        <Controller
                          name="napomena"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              fullWidth
                              label="Napomena"
                              multiline
                              minRows={2}
                              sx={{ bgcolor: '#fff9c4' }}
                            />
                          )}
                        />
                      </Box>
                      <Box>
                        <Controller
                          name="secena"
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              select
                              fullWidth
                              label="Sečena ili bez sečenja"
                              sx={{ bgcolor: '#fff9c4' }}
                            >
                              <MenuItem value="">Izaberi</MenuItem>
                              <MenuItem value="secena">Sečena</MenuItem>
                              <MenuItem value="bez_secena">Bez sečenja</MenuItem>
                            </TextField>
                          )}
                        />
                      </Box>
                      {watch('secena') === 'secena' && (
                        <Box>
                          <Controller
                            name="pozicijaSecenja"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                select
                                fullWidth
                                label="Pozicija sečenja"
                                sx={{ bgcolor: '#fff9c4' }}
                              >
                                <MenuItem value="DONJI DEO">DONJI DEO</MenuItem>
                                <MenuItem value="GORNJI DEO">GORNJI DEO</MenuItem>
                                <MenuItem value="LEVA STRANA">LEVA STRANA</MenuItem>
                                <MenuItem value="DESNA STRANA">DESNA STRANA</MenuItem>
                                <MenuItem value="GORNJA LEVA STRANA">GORNJA LEVA STRANA</MenuItem>
                                <MenuItem value="GORNJA DESNA STRANA">GORNJA DESNA STRANA</MenuItem>
                                <MenuItem value="DONJA LEVA STRANA">DONJA LEVA STRANA</MenuItem>
                                <MenuItem value="DONJA DESNA STRANA">DONJA DESNA STRANA</MenuItem>
                              </TextField>
                            )}
                          />
                        </Box>
                      )}
                    </>
                  )}
                </Box>
              </Paper>
            )}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Gustina
              </Typography>
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 2
              }}>
                <Box>
                  <Controller
                    name="masaRolne"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Masa rolne (kg)"
                        type="number" {...getNumberInputProps()}
                        inputProps={{ step: "0.001" }}
                        error={!!formState.errors.masaRolne}
                        helperText={formState.errors.masaRolne?.message}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        InputProps={{ readOnly: isLockedForTehnolog || (role === 'tehnolog' ? !isYellowField('masaRolne') : false) }}
                        sx={{ bgcolor: isYellowField('masaRolne') ? 'lightyellow' : undefined }}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Zapremina (m³)"
                    type="number" {...getNumberInputProps()}
                    value={zapreminaProizvoda}
                    InputProps={{ readOnly: true }}
                    sx={{ bgcolor: '#e3f2fd' }}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Gustina (kg/m³)"
                    type="number" {...getNumberInputProps()}
                    value={formatAdminNumber(gustinaProizvoda)}
                    InputProps={{ readOnly: true }}
                    sx={{
                      bgcolor: inRange(gustinaProizvoda, densityRange) === true ? '#c8e6c9' : '#ffcdd2'
                    }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Min: {densityRange.min} &nbsp; Max: {densityRange.max}
                  </Typography>
                </Box>
              </Box>
              {templateMode && (role === 'admin' || role === 'master_admin') && (
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <TextField label="Gustina min" type="number" {...getNumberInputProps()} value={densityRange.min} onChange={e => setDensityRange(r => ({ ...r, min: Number(e.target.value) }))} />
                  <TextField label="Gustina max" type="number" {...getNumberInputProps()} value={densityRange.max} onChange={e => setDensityRange(r => ({ ...r, max: Number(e.target.value) }))} />
                </Box>
              )}
            </Paper>
            
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Suva materija
              </Typography>
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' },
                gap: 2
              }}>
                <Box>
                  <Controller
                    name="m1"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="m1"
                        type="number" {...getNumberInputProps()}
                        inputProps={{ step: "0.001" }}
                        error={!!formState.errors.m1}
                        helperText={formState.errors.m1?.message}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        InputProps={{ readOnly: isLockedForTehnolog || (role === 'tehnolog' ? !isYellowField('m1') : false) }}
                        sx={{ bgcolor: isYellowField('m1') ? 'lightyellow' : undefined }}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Controller
                    name="m2"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="m2"
                        type="number" {...getNumberInputProps()}
                        inputProps={{ step: "0.001" }}
                        error={!!formState.errors.m2}
                        helperText={formState.errors.m2?.message}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        InputProps={{ readOnly: isLockedForTehnolog || (role === 'tehnolog' ? !isYellowField('m2') : false) }}
                        sx={{ bgcolor: isYellowField('m2') ? 'lightyellow' : undefined }}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Controller
                    name="m3"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="m3"
                        type="number" {...getNumberInputProps()}
                        inputProps={{ step: "0.001" }}
                        error={!!formState.errors.m3}
                        helperText={formState.errors.m3?.message}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        InputProps={{ readOnly: isLockedForTehnolog || (role === 'tehnolog' ? !isYellowField('m3') : false) }}
                        sx={{ bgcolor: isYellowField('m3') ? 'lightyellow' : undefined }}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="m4 (m2 - m3)"
                    type="number" {...getNumberInputProps()}
                    value={formatAdminNumber(suvaMaterijaCalculations.m4)}
                    InputProps={{ readOnly: true }}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="% SM"
                    type="number" {...getNumberInputProps()}
                    value={formatAdminNumber(suvaMaterijaCalculations.procenatSM)}
                    InputProps={{ readOnly: true }}
                    sx={{
                      bgcolor: inRange(suvaMaterijaCalculations.procenatSM, smRange) === true ? '#c8e6c9' : '#ffcdd2'
                    }}
                  />
                </Box>
              </Box>
              {templateMode && (role === 'admin' || role === 'master_admin') && (
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <TextField label="%SM min" type="number" {...getNumberInputProps()} value={smRange.min} onChange={e => setSmRange(r => ({ ...r, min: Number(e.target.value) }))} />
                  <TextField label="%SM max" type="number" {...getNumberInputProps()} value={smRange.max} onChange={e => setSmRange(r => ({ ...r, max: Number(e.target.value) }))} />
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                %SM min: {smRange.min} &nbsp; max: {smRange.max}
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Izmerene vrednosti dužine (mm)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Na širini (mm)</TableCell>
                      <TableCell>Izmerena vrednost (mm)</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(templateMode ? templateDuzina : duzineRows).map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <TextField
                            type="number" {...getNumberInputProps()}
                            size="small"
                            value={row.duzina}
                            onChange={e => (templateMode && (role === 'admin' || role === 'master_admin'))
                              ? setTemplateDuzina(prev => prev.map((r, i) => i === idx ? { ...r, duzina: parseFloat(e.target.value) || 0 } : r))
                              : handleDuzinaRowChange(idx, 'duzina', parseFloat(e.target.value) || 0)
                            }
                            inputProps={{ step: "0.1" }}
                            InputProps={{ readOnly: !(templateMode && (role === 'admin' || role === 'master_admin')) }}
                            sx={templateMode && (role === 'admin' || role === 'master_admin') ? { bgcolor: 'lightyellow' } : {}}
                          />
                        </TableCell>
                        <TableCell>
                          {templateMode ? (
                            <TextField
                              type="number" {...getNumberInputProps()}
                              size="small"
                              value={(row as DuzinaRow).izmerena !== undefined && (row as DuzinaRow).izmerena !== null ? (row as DuzinaRow).izmerena : ''}
                              onChange={e => handleDuzinaRowChange(idx, 'izmerena', e.target.value)}
                              inputProps={{ step: "0.1" }}
                              sx={{ bgcolor: 'lightyellow' }}
                            />
                          ) : (
                            <TextField
                              type="number" {...getNumberInputProps()}
                              size="small"
                              value={(row as DuzinaRow).izmerena !== undefined && (row as DuzinaRow).izmerena !== null ? (row as DuzinaRow).izmerena : ''}
                              onChange={e => handleDuzinaRowChange(idx, 'izmerena', e.target.value)}
                              inputProps={{ step: "0.1" }}
                              sx={{ bgcolor: 'lightyellow' }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {(templateMode || (role === 'admin' || role === 'master_admin')) && (
                            <IconButton onClick={() => templateMode
                              ? setTemplateDuzina(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev)
                              : removeDuzinaRow(idx)}>
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell align="right"><b>Srednja vrednost (mm):</b></TableCell>
                      <TableCell colSpan={2}>
                        <TextField size="small" value={avgDuzinaDynamic !== undefined && avgDuzinaDynamic !== null ? String(avgDuzinaDynamic) : ''} InputProps={{ readOnly: true }}
                          sx={((role === 'admin' || role === 'master_admin') || role === 'tehnolog') ? {
                            bgcolor: inRange(avgDuzinaDynamic, lengthRange) === true ? '#c8e6c9' : '#ffcdd2'
                          } : {}}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              {templateMode && (role === 'admin' || role === 'master_admin') && (
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <TextField label="Dužina min" type="number" {...getNumberInputProps()} value={lengthRange.min} onChange={e => setLengthRange(r => ({ ...r, min: Number(e.target.value) }))} />
                  <TextField label="Dužina max" type="number" {...getNumberInputProps()} value={lengthRange.max} onChange={e => setLengthRange(r => ({ ...r, max: Number(e.target.value) }))} />
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Dužina min: {lengthRange.min} &nbsp; max: {lengthRange.max}
              </Typography>
              {templateMode && (
                <Box sx={{ mt: 2 }}>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setTemplateDuzina(prev => [...prev, { duzina: 0 }])}>
                    Dodaj red
                  </Button>
                </Box>
              )}
            </Paper>
            
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Izmerene vrednosti širine (mm)
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Na dužini (mm)</TableCell>
                      <TableCell>Izmerena vrednost (mm)</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(templateMode ? templateSirina : sirineRows).map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <TextField
                            type="number" {...getNumberInputProps()}
                            size="small"
                            value={row.sirina}
                            onChange={e => (templateMode && (role === 'admin' || role === 'master_admin'))
                              ? setTemplateSirina(prev => prev.map((r, i) => i === idx ? { ...r, sirina: parseFloat(e.target.value) || 0 } : r))
                              : handleSirinaRowChange(idx, 'sirina', parseFloat(e.target.value) || 0)
                            }
                            inputProps={{ step: "0.1" }}
                            InputProps={{ readOnly: !(templateMode && (role === 'admin' || role === 'master_admin')) }}
                            sx={templateMode && (role === 'admin' || role === 'master_admin') ? { bgcolor: 'lightyellow' } : {}}
                          />
                        </TableCell>
                        <TableCell>
                          {templateMode ? (
                            <TextField
                              type="number" {...getNumberInputProps()}
                              size="small"
                              value={(row as SirinaRow).izmerena !== undefined && (row as SirinaRow).izmerena !== null ? (row as SirinaRow).izmerena : ''}
                              onChange={e => handleSirinaRowChange(idx, 'izmerena', e.target.value)}
                              inputProps={{ step: "0.1" }}
                              sx={{ bgcolor: 'lightyellow' }}
                            />
                          ) : (
                            <TextField
                              type="number" {...getNumberInputProps()}
                              size="small"
                              value={(row as SirinaRow).izmerena !== undefined && (row as SirinaRow).izmerena !== null ? (row as SirinaRow).izmerena : ''}
                              onChange={e => handleSirinaRowChange(idx, 'izmerena', e.target.value)}
                              inputProps={{ step: "0.1" }}
                              sx={{ bgcolor: 'lightyellow' }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          {(templateMode || (role === 'admin' || role === 'master_admin')) && (
                            <IconButton onClick={() => templateMode
                              ? setTemplateSirina(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev)
                              : removeSirinaRow(idx)}>
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell align="right"><b>Srednja vrednost (mm):</b></TableCell>
                      <TableCell colSpan={2}>
                        <TextField size="small" value={avgSirinaDynamic !== undefined && avgSirinaDynamic !== null ? String(avgSirinaDynamic) : ''} InputProps={{ readOnly: true }}
                          sx={((role === 'admin' || role === 'master_admin') || role === 'tehnolog') ? {
                            bgcolor: inRange(avgSirinaDynamic, widthRange) === true ? '#c8e6c9' : '#ffcdd2'
                          } : {}}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              {templateMode && (role === 'admin' || role === 'master_admin') && (
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <TextField label="Širina min" type="number" {...getNumberInputProps()} value={widthRange.min} onChange={e => setWidthRange(r => ({ ...r, min: Number(e.target.value) }))} />
                  <TextField label="Širina max" type="number" {...getNumberInputProps()} value={widthRange.max} onChange={e => setWidthRange(r => ({ ...r, max: Number(e.target.value) }))} />
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Širina min: {widthRange.min} &nbsp; max: {widthRange.max}
              </Typography>
              {templateMode && (
                <Box sx={{ mt: 2 }}>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setTemplateSirina(prev => [...prev, { sirina: 0 }])}>
                    Dodaj red
                  </Button>
                </Box>
              )}
            </Paper>
            
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Distribucija vlakana
              </Typography>
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(4, 1fr)' },
                gap: 2
              }}>
                <Box>
                  <Controller
                    name="distribucija1"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="1"
                        type="number" {...getNumberInputProps()}
                        inputProps={{ step: "0.1" }}
                        error={!!formState.errors.distribucija1}
                        helperText={formState.errors.distribucija1?.message}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        InputProps={{ readOnly: isLockedForTehnolog || (role === 'tehnolog' ? !isYellowField('distribucija1') : false) }}
                        sx={{ bgcolor: isYellowField('distribucija1') ? 'lightyellow' : undefined }}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Controller
                    name="distribucija2"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="2"
                        type="number" {...getNumberInputProps()}
                        inputProps={{ step: "0.1" }}
                        error={!!formState.errors.distribucija2}
                        helperText={formState.errors.distribucija2?.message}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        InputProps={{ readOnly: isLockedForTehnolog || (role === 'tehnolog' ? !isYellowField('distribucija2') : false) }}
                        sx={{ bgcolor: isYellowField('distribucija2') ? 'lightyellow' : undefined }}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Controller
                    name="distribucija3"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="3"
                        type="number" {...getNumberInputProps()}
                        inputProps={{ step: "0.1" }}
                        error={!!formState.errors.distribucija3}
                        helperText={formState.errors.distribucija3?.message}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        InputProps={{ readOnly: isLockedForTehnolog || (role === 'tehnolog' ? !isYellowField('distribucija3') : false) }}
                        sx={{ bgcolor: isYellowField('distribucija3') ? 'lightyellow' : undefined }}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Controller
                    name="distribucija4"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="4"
                        type="number" {...getNumberInputProps()}
                        inputProps={{ step: "0.1" }}
                        error={!!formState.errors.distribucija4}
                        helperText={formState.errors.distribucija4?.message}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        InputProps={{ readOnly: isLockedForTehnolog || (role === 'tehnolog' ? !isYellowField('distribucija4') : false) }}
                        sx={{ bgcolor: isYellowField('distribucija4') ? 'lightyellow' : undefined }}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Controller
                    name="distribucija5"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="5"
                        type="number" {...getNumberInputProps()}
                        inputProps={{ step: "0.1" }}
                        error={!!formState.errors.distribucija5}
                        helperText={formState.errors.distribucija5?.message}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        InputProps={{ readOnly: isLockedForTehnolog || (role === 'tehnolog' ? !isYellowField('distribucija5') : false) }}
                        sx={{ bgcolor: isYellowField('distribucija5') ? 'lightyellow' : undefined }}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Controller
                    name="distribucija6"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="6"
                        type="number" {...getNumberInputProps()}
                        inputProps={{ step: "0.1" }}
                        error={!!formState.errors.distribucija6}
                        helperText={formState.errors.distribucija6?.message}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        InputProps={{ readOnly: isLockedForTehnolog || (role === 'tehnolog' ? !isYellowField('distribucija6') : false) }}
                        sx={{ bgcolor: isYellowField('distribucija6') ? 'lightyellow' : undefined }}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <TextField
                    fullWidth
                    label="Srednja vrednost distribucije vlakana"
                    type="number" {...getNumberInputProps()}
                    value={formatAdminNumber(avgDistribucija)}
                    InputProps={{ readOnly: true }}
                    sx={{
                      bgcolor: inRange(avgDistribucija, fiberDistRange) === true ? '#c8e6c9' : '#ffcdd2'
                    }}
                  />
                </Box>
              </Box>
              {templateMode && (role === 'admin' || role === 'master_admin') && (
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <TextField label="Distribucija vlakana min" type="number" {...getNumberInputProps()} value={fiberDistRange.min} onChange={e => setFiberDistRange(r => ({ ...r, min: Number(e.target.value) }))} />
                  <TextField label="Distribucija vlakana max" type="number" {...getNumberInputProps()} value={fiberDistRange.max} onChange={e => setFiberDistRange(r => ({ ...r, max: Number(e.target.value) }))} />
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Distribucija vlakana min: {fiberDistRange.min} &nbsp; max: {fiberDistRange.max}
              </Typography>
            </Paper>
            
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Proračun Debljine
              </Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Tip</TableCell>
                      <TableCell>Tačka</TableCell>
                      <TableCell>Dužina (mm)</TableCell>
                      <TableCell>Širina (mm)</TableCell>
                      <TableCell>Izmerena vrednost</TableCell>
                      <TableCell>Šubler</TableCell>
                      <TableCell>Stvarna debljina</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(templateMode ? templateDebljina : rows).map((row, idx) => (
                      <TableRow key={idx}>
                        <TableCell>
                          <Select
                            value={row.type}
                            onChange={e => {
                              if ((role === 'admin' || role === 'master_admin')) {
                                const newType = e.target.value as 'D' | 'L';
                                // automatski promeni labelu na sledeći slobodan D ili L
                                const existingLabels = templateDebljina.map(r => r.label);
                                let count = 1;
                                while (existingLabels.includes(`${newType}${count}`)) {
                                  count++;
                                }
                                setTemplateDebljina(prev => prev.map((r, i) =>
                                  i === idx
                                    ? { ...r, type: newType, label: `${newType}${count}`, measured: r.measured ?? '', caliper: r.caliper ?? '' }
                                    : r
                                ));
                              }
                            }}
                            size="small"
                            disabled={role !== 'admin'}
                          >
                            <MenuItem value="D">D</MenuItem>
                            <MenuItem value="L">L</MenuItem>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={row.label}
                            onChange={e => (role === 'admin' || role === 'master_admin') && setTemplateDebljina(prev => prev.map((r, i) => i === idx ? { ...r, label: e.target.value } : r))}
                            InputProps={{ readOnly: role !== 'admin' }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number" {...getNumberInputProps()}
                            size="small"
                            value={row.length}
                            onChange={e => (templateMode && (role === 'admin' || role === 'master_admin')) ? setTemplateDebljina(prev => prev.map((r, i) => i === idx ? { ...r, length: Number(e.target.value) } : r)) : (role === 'admin' || role === 'master_admin') ? setTemplateDebljina(prev => prev.map((r, i) => i === idx ? { ...r, length: Number(e.target.value) } : r)) : null}
                            InputProps={{ readOnly: !(templateMode && (role === 'admin' || role === 'master_admin')) }}
                            sx={templateMode && (role === 'admin' || role === 'master_admin') ? { bgcolor: 'lightyellow' } : undefined}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number" {...getNumberInputProps()}
                            size="small"
                            value={row.width}
                            onChange={e => (templateMode && (role === 'admin' || role === 'master_admin')) ? setTemplateDebljina(prev => prev.map((r, i) => i === idx ? { ...r, width: Number(e.target.value) } : r)) : (role === 'admin' || role === 'master_admin') ? setTemplateDebljina(prev => prev.map((r, i) => i === idx ? { ...r, width: Number(e.target.value) } : r)) : null}
                            InputProps={{ readOnly: !(templateMode && (role === 'admin' || role === 'master_admin')) }}
                            sx={templateMode && (role === 'admin' || role === 'master_admin') ? { bgcolor: 'lightyellow' } : undefined}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number" {...getNumberInputProps()}
                            size="small"
                            value={row.measured !== undefined && row.measured !== null ? row.measured : ''}
                            onChange={e => handleRowChange(idx, 'measured', e.target.value)}
                            sx={{ bgcolor: 'lightyellow' }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number" {...getNumberInputProps()}
                            size="small"
                            value={row.caliper !== undefined && row.caliper !== null ? row.caliper : ''}
                            onChange={e => handleRowChange(idx, 'caliper', e.target.value)}
                            sx={{ bgcolor: 'lightyellow' }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            size="small"
                            value={templateMode ? '' : formatAdminNumber(actualThickness[idx])}
                            InputProps={{ readOnly: true }}
                          />
                        </TableCell>
                        <TableCell>
                          {(templateMode || (role === 'admin' || role === 'master_admin')) && (
                            <IconButton
                              onClick={() => templateMode
                                ? setTemplateDebljina(prev => prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev)
                                : removeRow(idx)}>
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow>
                      <TableCell align="right" colSpan={6}><b>Srednja vrednost:</b></TableCell>
                      <TableCell colSpan={2}>
                        <TextField size="small" value={templateMode ? '' : formatAdminNumber(avgThickness !== undefined && avgThickness !== null ? avgThickness : '')} InputProps={{ readOnly: true }}
                          sx={((role === 'admin' || role === 'master_admin') || role === 'tehnolog') ? {
                            bgcolor: inRange(avgThickness, thicknessRange) === true ? '#c8e6c9' : '#ffcdd2'
                          } : {}}
                        />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
              {templateMode && (role === 'admin' || role === 'master_admin') && (
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <TextField label="Debljina min" type="number" {...getNumberInputProps()} value={thicknessRange.min} onChange={e => setThicknessRange(r => ({ ...r, min: Number(e.target.value) }))} />
                  <TextField label="Debljina max" type="number" {...getNumberInputProps()} value={thicknessRange.max} onChange={e => setThicknessRange(r => ({ ...r, max: Number(e.target.value) }))} />
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                Debljina min: {thicknessRange.min} &nbsp; max: {thicknessRange.max}
              </Typography>
              {templateMode && (
                <Box sx={{ mt: 2 }}>
                  <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setTemplateDebljina(prev => [...prev, { type: 'D' as 'D', label: `D${prev.length + 1}`, length: 0, width: 0, measured: '', caliper: '' }])}>
                    Dodaj red
                  </Button>
                </Box>
              )}
            </Paper>
            
            {!hideReferentnaVrednost && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Referentna vrednost &gt; 1 kPa s/m²
              </Typography>
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                gap: 2
              }}>
                <Box>
                  <Controller
                    name="debljina1"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Debljina 1"
                        type="number" {...getNumberInputProps()}
                        inputProps={{ step: "0.001" }}
                        error={!!formState.errors.debljina1}
                        helperText={formState.errors.debljina1?.message}
                        InputProps={{ readOnly: true }}
                        sx={{ bgcolor: isYellowField('debljina1') ? 'lightyellow' : undefined }}
                      />
                    )}
                  />
                </Box>
                <Box>
                  <Controller
                    name="pretvaranje"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Pretvaranje"
                        type="number" {...getNumberInputProps()}
                        inputProps={{ step: "0.001" }}
                        error={!!formState.errors.pretvaranje}
                        helperText={formState.errors.pretvaranje?.message}
                        InputProps={{ readOnly: true }}
                        sx={{ bgcolor: isYellowField('pretvaranje') ? 'lightyellow' : undefined }}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Paper>
            )}
            
            <Paper sx={{ p: 3 }}>
              <FormControl component="fieldset" fullWidth sx={{ m: 0, p: 0 }}>
                <FormLabel component="legend" sx={{ fontSize: 18, mb: 2, color: 'text.primary' }}>
                  AFr - UZORAK 1
                </FormLabel>
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: 2
                }}>
                  <Box>
                    <Controller
                      name="debljinaUzorak1"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Debljina, uzorak 1"
                          type="number" {...getNumberInputProps()}
                          inputProps={{ step: "0.001" }}
                          error={!!formState.errors.debljinaUzorak1}
                          helperText={formState.errors.debljinaUzorak1?.message}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          InputProps={{ readOnly: isLockedForTehnolog || (role === 'tehnolog' ? !isYellowField('debljinaUzorak1') : false) }}
                          sx={{ bgcolor: isYellowField('debljinaUzorak1') ? 'lightyellow' : undefined }}
                        />
                      )}
                    />
                  </Box>
                  <Box>
                    <Controller
                      name="deltaP1"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="ΔP (Pa)"
                          type="number" {...getNumberInputProps()}
                          inputProps={{ step: "0.001" }}
                          error={!!formState.errors.deltaP1}
                          helperText={formState.errors.deltaP1?.message}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          InputProps={{ readOnly: isLockedForTehnolog || (role === 'tehnolog' ? !isYellowField('deltaP1') : false) }}
                          sx={{ bgcolor: isYellowField('deltaP1') ? 'lightyellow' : undefined }}
                        />
                      )}
                    />
                  </Box>
                  {!hideAfrDetails && (
                    <>
                      <Box>
                        <TextField
                          fullWidth
                          label="d (m)"
                          type="number" {...getNumberInputProps()}
                          value={formatAdminNumber(afrCalculations.afr1?.d)}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          label="R (Pa*s/m³)"
                          type="number" {...getNumberInputProps()}
                          value={formatAdminNumber(afrCalculations.afr1?.R)}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          label="A (m²)"
                          type="number" {...getNumberInputProps()}
                          value="0.01"
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          label="Rs (Pa*s/m)"
                          type="number" {...getNumberInputProps()}
                          value={formatAdminNumber(afrCalculations.afr1?.Rs)}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          label="r (Pa*s/m²)"
                          type="number" {...getNumberInputProps()}
                          value={formatAdminNumber(afrCalculations.afr1?.r)}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                    </>
                  )}
                  <Box>
                    <TextField
                      fullWidth
                      label="Afr (kPa*s/m²)"
                      type="number" {...getNumberInputProps()}
                      value={formatAdminNumber(afrCalculations.afr1?.Afr)}
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                      sx={((role === 'admin' || role === 'master_admin') || role === 'tehnolog') ? {
                        bgcolor: inRange(afrCalculations.afr1?.Afr, { min: afrMin }) === true ? '#c8e6c9' : '#ffcdd2'
                      } : {}}
                    />
                  </Box>
                </Box>
                {/* Uklonjeni svi inputi i caption za min/max, uključujući zakomentarisane */}
              </FormControl>
            </Paper>
            
            <Paper sx={{ p: 3 }}>
              <FormControl component="fieldset" fullWidth sx={{ m: 0, p: 0 }}>
                <FormLabel component="legend" sx={{ fontSize: 18, mb: 2, color: 'text.primary' }}>
                  AFr - UZORAK 2
                </FormLabel>
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: 2
                }}>
                  <Box>
                    <Controller
                      name="debljinaUzorak2"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Debljina, uzorak 2"
                          type="number" {...getNumberInputProps()}
                          inputProps={{ step: "0.001" }}
                          error={!!formState.errors.debljinaUzorak2}
                          helperText={formState.errors.debljinaUzorak2?.message}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          InputProps={{ readOnly: isLockedForTehnolog || (role === 'tehnolog' ? !isYellowField('debljinaUzorak2') : false) }}
                          sx={{ bgcolor: isYellowField('debljinaUzorak2') ? 'lightyellow' : undefined }}
                        />
                      )}
                    />
                  </Box>
                  <Box>
                    <Controller
                      name="deltaP2"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="ΔP (Pa)"
                          type="number" {...getNumberInputProps()}
                          inputProps={{ step: "0.001" }}
                          error={!!formState.errors.deltaP2}
                          helperText={formState.errors.deltaP2?.message}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          InputProps={{ readOnly: isLockedForTehnolog || (role === 'tehnolog' ? !isYellowField('deltaP2') : false) }}
                          sx={{ bgcolor: isYellowField('deltaP2') ? 'lightyellow' : undefined }}
                        />
                      )}
                    />
                  </Box>
                  {!hideAfrDetails && (
                    <>
                      <Box>
                        <TextField
                          fullWidth
                          label="d (m)"
                          type="number" {...getNumberInputProps()}
                          value={formatAdminNumber(afrCalculations.afr2?.d)}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          label="R (Pa*s/m³)"
                          type="number" {...getNumberInputProps()}
                          value={formatAdminNumber(afrCalculations.afr2?.R)}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          label="A (m²)"
                          type="number" {...getNumberInputProps()}
                          value="0.01"
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          label="Rs (Pa*s/m)"
                          type="number" {...getNumberInputProps()}
                          value={formatAdminNumber(afrCalculations.afr2?.Rs)}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          label="r (Pa*s/m²)"
                          type="number" {...getNumberInputProps()}
                          value={formatAdminNumber(afrCalculations.afr2?.r)}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                    </>
                  )}
                  <Box>
                    <TextField
                      fullWidth
                      label="Afr (kPa*s/m²)"
                      type="number" {...getNumberInputProps()}
                      value={formatAdminNumber(afrCalculations.afr2?.Afr)}
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                      sx={((role === 'admin' || role === 'master_admin') || role === 'tehnolog') ? {
                        bgcolor: inRange(afrCalculations.afr2?.Afr, { min: afrMin }) === true ? '#c8e6c9' : '#ffcdd2'
                      } : {}}
                    />
                  </Box>
                </Box>
                {/* Uklonjeni svi inputi i caption za min/max, uključujući zakomentarisane */}
              </FormControl>
            </Paper>
            
            <Paper sx={{ p: 3 }}>
              <FormControl component="fieldset" fullWidth sx={{ m: 0, p: 0 }}>
                <FormLabel component="legend" sx={{ fontSize: 18, mb: 2, color: 'text.primary' }}>
                  AFr - UZORAK 3
                </FormLabel>
                <Box sx={{ 
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                  gap: 2
                }}>
                  <Box>
                    <Controller
                      name="debljinaUzorak3"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="Debljina, uzorak 3"
                          type="number" {...getNumberInputProps()}
                          inputProps={{ step: "0.001" }}
                          error={!!formState.errors.debljinaUzorak3}
                          helperText={formState.errors.debljinaUzorak3?.message}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          InputProps={{ readOnly: isLockedForTehnolog || (role === 'tehnolog' ? !isYellowField('debljinaUzorak3') : false) }}
                          sx={{ bgcolor: isYellowField('debljinaUzorak3') ? 'lightyellow' : undefined }}
                        />
                      )}
                    />
                  </Box>
                  <Box>
                    <Controller
                      name="deltaP3"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          fullWidth
                          label="ΔP (Pa)"
                          type="number" {...getNumberInputProps()}
                          inputProps={{ step: "0.001" }}
                          error={!!formState.errors.deltaP3}
                          helperText={formState.errors.deltaP3?.message}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                          InputProps={{ readOnly: isLockedForTehnolog || (role === 'tehnolog' ? !isYellowField('deltaP3') : false) }}
                          sx={{ bgcolor: isYellowField('deltaP3') ? 'lightyellow' : undefined }}
                        />
                      )}
                    />
                  </Box>
                  {!hideAfrDetails && (
                    <>
                      <Box>
                        <TextField
                          fullWidth
                          label="d (m)"
                          type="number" {...getNumberInputProps()}
                          value={formatAdminNumber(afrCalculations.afr3?.d)}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          label="R (Pa*s/m³)"
                          type="number" {...getNumberInputProps()}
                          value={formatAdminNumber(afrCalculations.afr3?.R)}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          label="A (m²)"
                          type="number" {...getNumberInputProps()}
                          value="0.01"
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          label="Rs (Pa*s/m)"
                          type="number" {...getNumberInputProps()}
                          value={formatAdminNumber(afrCalculations.afr3?.Rs)}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                      <Box>
                        <TextField
                          fullWidth
                          label="r (Pa*s/m²)"
                          type="number" {...getNumberInputProps()}
                          value={formatAdminNumber(afrCalculations.afr3?.r)}
                          InputProps={{ readOnly: true }}
                        />
                      </Box>
                    </>
                  )}
                  <Box>
                    <TextField
                      fullWidth
                      label="Afr (kPa*s/m²)"
                      type="number" {...getNumberInputProps()}
                      value={formatAdminNumber(afrCalculations.afr3?.Afr)}
                      InputProps={{ readOnly: true }}
                      InputLabelProps={{ shrink: true }}
                      sx={((role === 'admin' || role === 'master_admin') || role === 'tehnolog') ? {
                        bgcolor: inRange(afrCalculations.afr3?.Afr, { min: afrMin }) === true ? '#c8e6c9' : '#ffcdd2'
                      } : {}}
                    />
                  </Box>
                </Box>
                {/* Uklonjeni svi inputi i caption za min/max, uključujući zakomentarisane */}
              </FormControl>
            </Paper>
            
            {/* AFr srednja vrednost */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                AFr srednja vrednost
              </Typography>
              <Box>
                <TextField
                  fullWidth
                  label="Afr (kPa*s/m²)"
                  type="number" {...getNumberInputProps()}
                  value={formatAdminNumber(afrCalculations.avgAFr)}
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                  sx={((role === 'admin' || role === 'master_admin') || role === 'tehnolog') ? {
                    bgcolor: inRange(afrCalculations.avgAFr, { min: afrMin }) === true ? '#c8e6c9' : '#ffcdd2'
                  } : {}}
                />
              </Box>
              {templateMode && (role === 'admin' || role === 'master_admin') && (
                <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                  <TextField
                    label="AFr min"
                    type="number"
                    {...getNumberInputProps()}
                    value={afrMin}
                    onChange={e => setAfrRange({ min: Number(e.target.value) })}
                  />
                </Box>
              )}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                AFr min: {afrMin} (nema maksimum, mora biti veće od 1)
              </Typography>
            </Paper>

            {/* Toplotna vrednost */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Toplotna vrednost
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
                {/* λ Vrednost */}
                <Controller
                  name="lambdaVrednost"
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <TextField
                        {...field}
                        fullWidth
                        label="λ Vrednost"
                        type="number"
                        sx={{
                          bgcolor: getLambdaInputColor(watch('lambdaVrednost'), lambdaRefValue)
                        }}
                        onChange={e => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                        onMouseDown={e => { e.preventDefault(); (e.target as HTMLInputElement).select(); }}
                        onWheel={e => (e.target as HTMLInputElement).blur()}
                        onKeyDown={e => {
                          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                            e.preventDefault();
                            window.scrollBy({ top: e.key === 'ArrowDown' ? 40 : -40, behavior: 'smooth' });
                          }
                        }}
                      />
                      {lambdaRefValue !== null && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          Tolerancija: ≤ {lambdaRefValue}
                        </Typography>
                      )}
                    </Box>
                  )}
                />

                {/* λ Gustinga */}
                <Controller
                  name="lambdaGustinga"
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <TextField
                        {...field}
                        fullWidth
                        label="λ Gustinga"
                        type="number"
                        sx={{ bgcolor: '#fff9c4' }}
                        onChange={e => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                        onMouseDown={e => { e.preventDefault(); (e.target as HTMLInputElement).select(); }}
                        onWheel={e => (e.target as HTMLInputElement).blur()}
                        onKeyDown={e => {
                          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                            e.preventDefault();
                            window.scrollBy({ top: e.key === 'ArrowDown' ? 40 : -40, behavior: 'smooth' });
                          }
                        }}
                      />
                    </Box>
                  )}
                />

                {/* Lambda 90/90 */}
                <Controller
                  name="lambda9090"
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <TextField
                        {...field}
                        fullWidth
                        label="Lambda 90/90"
                        type="number" {...getNumberInputProps()}
                        InputProps={{ inputProps: { min: 0, step: 'any' } }}
                        sx={{ bgcolor: '#fff9c4' }}
                        onChange={e => field.onChange(e.target.value === '' ? undefined : parseFloat(e.target.value))}
                        value={field.value === null || field.value === undefined ? '' : field.value}
                      />
                    </Box>
                  )}
                />
              </Box>
            </Paper>

            {/* Kidalica */}
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Kidalica
              </Typography>
              <Box sx={{ maxWidth: 300 }}>
                <Controller
                  name="cs10"
                  control={control}
                  render={({ field }) => (
                    <Box>
                      <TextField
                        {...field}
                        fullWidth
                        label="CS (10)"
                        type="number"
                        sx={{
                          bgcolor: getCs10InputColor(watch('cs10'), csToleranceValue)
                        }}
                        onChange={e => field.onChange(e.target.value === '' ? null : parseFloat(e.target.value))}
                        onMouseDown={e => { e.preventDefault(); (e.target as HTMLInputElement).select(); }}
                        onWheel={e => (e.target as HTMLInputElement).blur()}
                        onKeyDown={e => {
                          if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                            e.preventDefault();
                            window.scrollBy({ top: e.key === 'ArrowDown' ? 40 : -40, behavior: 'smooth' });
                          }
                        }}
                      />
                      {csToleranceValue !== null && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          Tolerancija: &gt; {csToleranceValue}
                        </Typography>
                      )}
                    </Box>
                  )}
                />
              </Box>
            </Paper>

            {templateMode && (role === 'admin' || role === 'master_admin') && (
              <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                <TextField
                  label="λ tolerancija"
                  type="number"
                  {...getNumberInputProps()}
                  value={lambdaToleranceValue ?? ''}
                  onChange={e => setLambdaToleranceValue(Number(e.target.value))}
                />
                <TextField
                  label="CS tolerancija"
                  type="number"
                  {...getNumberInputProps()}
                  value={csToleranceValue}
                  onChange={e => setCsToleranceValue(Number(e.target.value))}
                />
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                onClick={handleBack}
                disabled={loading}
              >
                Otkaži
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                disabled={!formState.isDirty || !formState.isValid || loading}
              >
                Sačuvaj
              </Button>
            </Box>
            
            {/* Debug informacije */}
            {process.env.NODE_ENV === 'development' && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Forma je validna: {formState.isValid ? 'Da' : 'Ne'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Poruka/Ispraviti: {Object.keys(formState.errors).length}
                </Typography>
                {Object.keys(formState.errors).length > 0 && (
                  <Typography variant="body2" color="error">
                    Greške: {Object.keys(formState.errors).join(', ')}
                  </Typography>
                )}
              </Box>
            )}
            {(role === 'admin' || role === 'master_admin') && id && isKontrola && (
              <Box sx={{ mb: 2, display: 'flex', gap: 3, alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={watchedValues.verified}
                      onChange={e => {
                        setValue('verified', e.target.checked, { shouldDirty: true, shouldValidate: true });
                        if (e.target.checked) {
                          setValue('verifiedBy', userFromStorage.username || '', { shouldDirty: true, shouldValidate: true });
                          setValue('notVerified', false, { shouldDirty: true, shouldValidate: true });
                          setValue('notVerifiedBy', '', { shouldDirty: true, shouldValidate: true });
                        }
                      }}
                      disabled={!sviRezultatiZeleni()}
                    />
                  }
                  label="Verifikuj"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={watchedValues.notVerified}
                      onChange={e => {
                        setValue('notVerified', e.target.checked, { shouldDirty: true, shouldValidate: true });
                        if (e.target.checked) {
                          setValue('notVerifiedBy', userFromStorage.username || '', { shouldDirty: true, shouldValidate: true });
                          setValue('verified', false, { shouldDirty: true, shouldValidate: true });
                          setValue('verifiedBy', '', { shouldDirty: true, shouldValidate: true });
                        }
                      }}
                      disabled={sviRezultatiZeleni()}
                    />
                  }
                  label="Odbij"
                />
                {watchedValues.verified && watchedValues.verifiedBy && (
                  <Typography color="success.main" fontWeight={600}>
                    Verifikovao: {watchedValues.verifiedBy}
                  </Typography>
                )}
                {watchedValues.notVerified && watchedValues.notVerifiedBy && (
                  <Typography color="error.main" fontWeight={600}>
                    Odbio: {watchedValues.notVerifiedBy}
                  </Typography>
                )}
                {(!watchedValues.verified && !watchedValues.notVerified) && (
                  <Typography color="text.secondary" fontWeight={600}>
                    Status: Ceka pregled
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </form>
        {!templateMode && id && (role === 'admin' || role === 'master_admin') && (
          <Button variant="outlined" color="primary" onClick={() => setDialogOpen(true)} sx={{ mb: 2, ml: 2 }}>
            Učitaj šablon
          </Button>
        )}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <DialogTitle>Potvrda</DialogTitle>
          <DialogContent>
            <DialogContentText>
              Da li ste sigurni da želite za ovaj proizvod da primenite šablon?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)} color="inherit">Otkaži</Button>
            <Button onClick={async () => {
              setDialogOpen(false);
              await handleLoadTemplate();
              setSnackbarOpen(true); // Prikazi poruku korisniku
            }} color="primary" autoFocus>
              Primeni šablon
            </Button>
          </DialogActions>
        </Dialog>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3500}
          onClose={() => setSnackbarOpen(false)}
          message="Šablon je primenjen. Proverite i popunite sve potrebne podatke pa ručno sačuvajte."
        />
        
        {/* Edit Session Warning */}
        {editSession.warning && editSession.warning.currentEditor && (() => {
          const warning = editSession.warning;
          const sessionAge = warning.currentEditor?.lastActivity ? 
            Date.now() - new Date(warning.currentEditor.lastActivity).getTime() : 0;
          const isSessionActive = sessionAge <= 5 * 60 * 1000; // 5 minuta
          
          // Ako je sesija istekla, ne prikazuj upozorenje
          if (!isSessionActive) {
            // console.log('🔍 Session expired in ProductForm, clearing warning');
            editSession.warning = null;
            return null;
          }
          
          return (
            <EditSessionWarning
              open={!!warning}
              onClose={() => {
                // Otkaži - vrati na dashboard
                editSession.warning = null;
                navigate('/');
              }}
              onTakeOver={async () => {
                // Proveri da li je sesija stvarno aktivna pre take over-a
                if (warning?.currentEditor?.lastActivity) {
                  const sessionAge = Date.now() - new Date(warning.currentEditor.lastActivity).getTime();
                  if (sessionAge > 5 * 60 * 1000) { // 5 minuta
                    alert('Sesija je istekla. Pokušajte ponovo da započnete editovanje.');
                    editSession.warning = null;
                    return;
                  }
                }
                
                const success = await editSession.takeOverSession();
                if (success) {
                  // Zatvori upozorenje i nastavi sa editovanjem
                  editSession.warning = null;
                  // Osveži podatke forme da se prikaže trenutno stanje
                  if (id) {
                    await loadExistingData();
                  }
                  // Prikaži poruku o uspešnom preuzimanju i nastavi sa editovanjem
                  alert('Uspešno ste preuzeli kontrolu nad merenjem. Možete nastaviti sa editovanjem.');
                  // Forma će se automatski prebaciti u edit mod jer je setIsEditing(true) postavljen u takeOverSession
                }
              }}
              onContinue={() => {
                // Zatvori upozorenje i nastavi sa čekanjem - proveri ponovo za 5 sekundi
                editSession.warning = null;
                setTimeout(async () => {
                  await editSession.checkSession();
                  if (!editSession.warning) {
                    // Ako nema upozorenja, možemo da pokušamo da započnemo sesiju
                    await editSession.startSession();
                  }
                }, 5000);
              }}
              currentEditor={warning.currentEditor}
              resourceName={`Merenje ${id || 'novo'}`}
              canTakeOver={warning.canTakeOver}
              loading={editSession.loading}
            />
          );
        })()}

        {/* Session Taken Over Notification */}
        {editSession.sessionTakenOver && (
          <Dialog open={editSession.sessionTakenOver} onClose={() => {}}>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon color="warning" />
              Kontrola je preuzeta
            </DialogTitle>
            <DialogContent>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  Administrator je preuzeo kontrolu nad ovim merenjem.
                </Typography>
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Bicete preusmereni na dashboard.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button 
                onClick={() => {
                  // console.log('🔍 Session taken over dialog OK clicked');
                  editSession.sessionTakenOver = false;
                  navigate('/');
                }} 
                variant="contained"
              >
                OK
              </Button>
            </DialogActions>
          </Dialog>
        )}
      </Container>
    );
  }
);

export default ProductForm; 